import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import { coreAuth } from '../api/coreClient';
import { projectsApi, mapProjectsResponse } from '../api/projectsApi';
import {
  users as initialUsers,
  locations as initialLocations,
  inventory as initialInventory,
  purchaseOrders as initialPurchaseOrders,
  purchaseRequisitions as initialPurchaseRequisitions,
  shipments as initialShipments,
  movements as initialMovements,
  suppliers as initialSuppliers,
  notifications as initialNotifications,
  auditLog as initialAuditLog,
  costCenters as initialCostCenters,
  parks as initialParks,
  categories as initialCategories,
  projectBudgets as initialProjectBudgets,
  projectTimelines as initialProjectTimelines,
  tenders as initialTenders,
  apiEndpoints,
  lawyers as initialLawyers,
  legalCases as initialLegalCases,
  legalContracts as initialLegalContracts,
  legalComplianceMatrix as initialLegalCompliance,
  legalDocuments as initialLegalDocuments,
  legalPowers as initialLegalPowers,
  legalReports as initialLegalReports,
  rfqs as initialRfqs
} from '../data/seedZero';
import {
  projectStatusMatrix as initialProjectStatusMatrix,
  projectStatusCatalog,
  projectStageOrder,
  projectStatusProjects as initialProjectStatusProjects
} from '../data/projectStatusZero';
import {
  renewableProjects as initialRenewableProjects,
  renewablePermits as initialRenewablePermits,
  renewableRisks as initialRenewableRisks,
  renewableMilestones as initialRenewableMilestones,
  renewableStageLabels,
  renewableStages
} from '../data/renewablesZero';

import * as energyTemplates from '../data/energyTemplates';
import { t } from '../i18n';

const AppContext = createContext(null);

const STORAGE_KEY = 'alcor-erp-state-v3';
const DEFAULT_PASSWORD = 'admin123';
const isBrowser = typeof window !== 'undefined';

const COMMITMENT_STATUSES = new Set(['aprobada', 'enviada', 'recibida']);
const isOrderCommitted = (order) => COMMITMENT_STATUSES.has(order?.status);

const defaultChecklist = {
  tributariaVigente: false,
  sinJuiciosSanciones: false,
  seguridadIndustrial: false,
  sinAntecedentesCorrupcion: false
};

const defaultReviewNode = {
  status: 'pendiente',
  reviewerId: null,
  date: null,
  note: ''
};

const normalizeStatusLabel = (status) => projectStatusCatalog[status]?.label || status;
const normalizeStatusWeight = (status) =>
  projectStatusCatalog[status]?.weight != null ? projectStatusCatalog[status].weight : 0;

const classifyProjectControlType = (item) => {
  const reference = `${item?.category || ''} ${item?.name || ''}`.toLowerCase();
  if (
    reference.includes('permiso') ||
    reference.includes('autorización') ||
    reference.includes('autorizacion') ||
    reference.includes('registro') ||
    reference.includes('certificado') ||
    reference.includes('resolución')
  ) {
    return 'permiso';
  }
  if (reference.includes('contrato')) {
    return 'contrato';
  }
  if (reference.includes('informe') || reference.includes('plan') || reference.includes('evaluación')) {
    return 'estudio';
  }
  return 'gestión';
};

const resolveSemaphore = (status, expectedDate) => {
  const weight = normalizeStatusWeight(status);
  if (weight >= 100) return 'success';
  if (weight >= 60) return 'progress';
  if (!expectedDate) {
    return weight > 0 ? 'progress' : 'pending';
  }
  const today = new Date();
  const target = new Date(`${expectedDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) {
    return weight > 0 ? 'progress' : 'pending';
  }
  if (weight >= 60) {
    return today > target ? 'warning' : 'progress';
  }
  return today > target ? 'critical' : 'pending';
};

const createEmptySummary = () => ({
  total: 0,
  permits: 0,
  contracts: 0,
  studies: 0,
  completed: 0,
  inProgress: 0,
  pending: 0,
  critical: 0,
  onTrack: 0,
  overallProgress: 0
});

const RFQ_STATUS_META = {
  draft: { label: 'Borrador', next: ['received'] },
  received: { label: 'Recibida Compras', next: ['needs_correction', 'ready_to_assign'] },
  ready_to_assign: { label: 'Listo para asignar', next: ['assigned'] },
  needs_correction: { label: 'Pendiente Corrección', next: ['received'] },
  assigned: { label: 'Asignada a Comprador', next: ['in_quotation'] },
  in_quotation: { label: 'En licitación', next: ['evaluating'] },
  evaluating: { label: 'Evaluación proveedor', next: ['po_generated'] },
  po_generated: { label: 'PO generada', next: ['po_pending_approval'] },
  po_pending_approval: { label: 'PO en aprobación', next: ['po_adjustment', 'po_approved'] },
  po_adjustment: { label: 'PO con ajustes', next: ['po_pending_approval', 'cancelled'] },
  po_approved: { label: 'PO aprobada', next: ['po_sent'] },
  po_sent: { label: 'PO enviada', next: ['closed'] },
  closed: { label: 'Cerrada', next: [] },
  cancelled: { label: 'Cancelada', next: [] }
};

const getRfqStatusLabel = (status) => RFQ_STATUS_META[status]?.label || status;

const PROFILE_FALLBACK_BY_ROLE = {
  super_user: 'super_user',
  purchasing_manager: 'director',
  project_director: 'director',
  project_manager: 'gerente',
  area_manager: 'gerente',
  purchase_controller: 'gerente',
  contract_manager: 'gerente',
  abogado: 'gerente',
  comprador: 'operativo',
  office_staff: 'operativo',
  field_technician: 'operativo'
};
const PRIMARY_ROLE_BY_PROFILE = {
  operativo: 'comprador',
  gerente: 'project_manager',
  director: 'purchasing_manager',
  super_user: 'super_user'
};
const DEPRECATED_ROLES = new Set(['contador', 'tesorero', 'auditor', 'finance_admin']);

const getUserProfile = (user = {}) =>
  user.profile || PROFILE_FALLBACK_BY_ROLE[user.role] || 'operativo';

const ROLE_LANDING_PAGE = {
  purchasing_manager: 'dashboard',
  purchase_controller: 'dashboard',
  project_manager: 'dashboard',
  contract_manager: 'dashboard',
  super_user: 'dashboard',
  abogado: 'legal',
  office_staff: 'dashboard',
  field_technician: 'dashboard',
  comprador: 'dashboard'
};

const ROLE_WELCOME_HINT = {
  super_user: 'tienes control total sobre las configuraciones del Centro ERP.',
  purchasing_manager: 'repasa las aprobaciones y métricas de compras en curso.',
  purchase_controller: 'organiza las requisiciones y órdenes pendientes del equipo.',
  project_manager: 'supervisa la operación de proyectos y presupuestos activos.',
  abogado: 'estás listo para gestionar litigios, contratos estratégicos y cumplimiento legal.'
};

const applySupplierDefaults = (supplier) => {
  if (!supplier) return null;

  const normalizedStatus = supplier.status || 'Approved';

  const resolveReview = (review = {}, fallbackStatus = 'pendiente') => ({
    status: review.status || fallbackStatus,
    reviewerId: review.reviewerId || null,
    date: review.date || null,
    note: review.note || ''
  });

  let contractFallback = 'pendiente';
  let purchasingFallback = 'pendiente';

  if (normalizedStatus === 'Pending Approval') {
    contractFallback = 'aprobado';
  } else if (normalizedStatus === 'Approved') {
    contractFallback = 'aprobado';
    purchasingFallback = 'aprobado';
  } else if (normalizedStatus === 'Rejected') {
    contractFallback = 'rechazado';
    purchasingFallback = 'rechazado';
  }

  return {
    ...supplier,
    status: normalizedStatus,
    lastReviewedBy: supplier.lastReviewedBy || null,
    reviewedDate: supplier.reviewedDate || null,
    categories: Array.isArray(supplier.categories) ? supplier.categories : [],
    subcategories: Array.isArray(supplier.subcategories) ? supplier.subcategories : [],
    balanceLastTwoYears: Array.isArray(supplier.balanceLastTwoYears)
      ? supplier.balanceLastTwoYears
      : [],
    contacts: Array.isArray(supplier.contacts) ? supplier.contacts : [],
    documents: Array.isArray(supplier.documents) ? supplier.documents : [],
    history: Array.isArray(supplier.history) ? supplier.history : [],
    certifications: Array.isArray(supplier.certifications) ? supplier.certifications : [],
    complianceChecklist: {
      ...defaultChecklist,
      ...(supplier.complianceChecklist || {})
    },
    reviews: {
      contract: resolveReview(supplier.reviews?.contract, contractFallback),
      purchasing: resolveReview(supplier.reviews?.purchasing, purchasingFallback)
    },
    evaluation: {
      viability: supplier.evaluation?.viability || 'pendiente',
      score:
        supplier.evaluation?.score != null
          ? supplier.evaluation.score
          : supplier.complianceScore || 0,
      strengths: supplier.evaluation?.strengths || '',
      risks: supplier.evaluation?.risks || '',
      comments: supplier.evaluation?.comments || '',
      reviewerId: supplier.evaluation?.reviewerId || null,
      lastUpdated: supplier.evaluation?.lastUpdated || null,
      nextReview: supplier.evaluation?.nextReview || null
    }
  };
};

const applySuppliersDefaults = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .map((supplier) => applySupplierDefaults(supplier))
    .filter((supplier) => supplier !== null);
};

const normalizeUser = (user) => {
  if (!user) return user;
  return {
    ...user,
    password: DEFAULT_PASSWORD,
    profile: getUserProfile(user)
  };
};

const normalizeUsers = (list) => {
  if (!Array.isArray(list)) return [];
  return list
    .filter((user) => user && !DEPRECATED_ROLES.has(user.role))
    .map((user) => normalizeUser(user));
};

const mergeUsersWithBaseline = (baseline, persisted) => {
  if (!Array.isArray(persisted) || persisted.length === 0) {
    return baseline;
  }

  const merged = [...persisted];
  const existingIds = new Set(persisted.map((user) => user.id));

  baseline.forEach((user) => {
    if (!existingIds.has(user.id)) {
      merged.push(user);
    }
  });

  return merged;
};

const loadPersistedState = () => {
  if (!isBrowser) return null;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (parsed?.suppliers) {
      parsed.suppliers = applySuppliersDefaults(parsed.suppliers);
    }
    if (parsed?.users) {
      parsed.users = normalizeUsers(parsed.users);
    }
    const referenceUsers = parsed?.users || normalizeUsers(initialUsers);
    if (parsed?.currentUser) {
      parsed.currentUser = normalizeUser(parsed.currentUser);
      if (DEPRECATED_ROLES.has(parsed.currentUser.role)) {
        parsed.currentUser = null;
      }
    }
    if (parsed?.tenders) {
      parsed.tenders = parsed.tenders.map((tender) => normalizeTender(tender, referenceUsers));
    }
    return parsed;
  } catch (error) {
    console.warn('No fue posible cargar datos persistidos.', error);
    return null;
  }
};

const generateId = (prefix) => {
  const random = Math.random().toString(36).slice(2, 6);
  const timestamp = Date.now().toString(36).slice(-6);
  return `${prefix}-${random}${timestamp}`.toUpperCase();
};

const now = () => format(new Date(), 'yyyy-MM-dd HH:mm');

const ensureHistory = (orders) =>
  orders.map((order) => ({
    ...order,
    history: order.history || [
      {
        id: generateId('HIST'),
        action: 'Creación de orden',
        status: order.status,
        userId: order.createdBy,
        timestamp: order.createdDate,
        note: order.notes || ''
      }
    ]
  }));

const defaultLifecycleNode = () => ({
  status: 'pending',
  timestamp: null,
  userId: null,
  note: '',
  referenceId: null
});

const ensurePurchaseOrderLifecycle = (order) => {
  if (!order) return order;
  const base = order.lifecycle || {};
  return {
    ...order,
    lifecycle: {
      received: { ...defaultLifecycleNode(), ...base.received },
      invoiced: { ...defaultLifecycleNode(), ...base.invoiced },
      paid: { ...defaultLifecycleNode(), ...base.paid }
    }
  };
};

const ensureOrdersLifecycle = (orders) =>
  orders.map((order) => ensurePurchaseOrderLifecycle(order));

const RECEIVING_ALLOWED_ROLES = new Set(['comprador', 'purchasing_manager', 'super_user']);
const FINANCE_ALLOWED_ROLES = new Set(['purchasing_manager', 'project_manager', 'super_user']);

const applyLifecycleUpdate = (
  order,
  key,
  {
    userId,
    note = '',
    referenceId = null,
    timestamp = now(),
    status = 'completed'
  }
) => {
  const ensured = ensurePurchaseOrderLifecycle(order);
  return {
    ...ensured,
    lifecycle: {
      ...ensured.lifecycle,
      [key]: {
        ...ensured.lifecycle[key],
        status,
        timestamp,
        userId,
        note,
        referenceId
      }
    }
  };
};

const mergeCollectionsById = (baseline = [], incoming = []) => {
  if (!Array.isArray(incoming) || incoming.length === 0) return baseline;
  const map = new Map();
  baseline.forEach((item) => map.set(item.id, item));

  incoming.forEach((item) => {
    if (!item || !item.id) return;
    const existing = map.get(item.id);
    if (existing) {
      map.set(item.id, { ...existing, ...item });
    } else {
      map.set(item.id, item);
    }
  });

  const merged = baseline.map((item) => map.get(item.id));
  incoming.forEach((item) => {
    if (!baseline.find((base) => base.id === item.id)) {
      merged.push(map.get(item.id));
    }
  });
  return merged;
};

const normalizeAttachmentList = (attachments = []) => {
  if (!Array.isArray(attachments)) return [];
  return attachments.map((attachment) => ({
    id: attachment.id || generateId('ATT'),
    name: attachment.name || 'Documento',
    size: attachment.size || 0,
    type: attachment.type || 'application/octet-stream',
    uploadedAt: attachment.uploadedAt || now(),
    dataUrl: attachment.dataUrl || '',
    source: attachment.source || 'upload'
  }));
};

const getDefaultTenderApprovals = (usersList) => {
  const projectManager = usersList.find((user) => user.role === 'project_manager');
  const purchasingManager = usersList.find((user) => user.role === 'purchasing_manager');
  return {
    projectManager: {
      userId: projectManager?.id || null,
      status: 'pendiente',
      date: null,
      note: ''
    },
    purchasingManager: {
      userId: purchasingManager?.id || null,
      status: 'pendiente',
      date: null,
      note: ''
    }
  };
};

const normalizeTender = (tender, usersList) => {
  if (!tender) return tender;
  const defaultApprovals = getDefaultTenderApprovals(usersList);
  const approvals = tender.approvals || defaultApprovals;
  return {
    ...tender,
    attachments: normalizeAttachmentList(tender.attachments || []),
    approvals: {
      projectManager: {
        ...approvals.projectManager,
        status: approvals.projectManager?.status || 'pendiente',
        date: approvals.projectManager?.date || null,
        note: approvals.projectManager?.note || '',
        userId: approvals.projectManager?.userId || defaultApprovals.projectManager.userId
      },
      purchasingManager: {
        ...approvals.purchasingManager,
        status: approvals.purchasingManager?.status || 'pendiente',
        date: approvals.purchasingManager?.date || null,
        note: approvals.purchasingManager?.note || '',
        userId: approvals.purchasingManager?.userId || defaultApprovals.purchasingManager.userId
      }
    },
    generatedOrderId: tender.generatedOrderId || null
  };
};

const computeNextTenderCode = (currentTenders = []) => {
  const year = format(new Date(), 'yyyy');
  const sequences = currentTenders
    .map((tender) => tender.tenderNumber)
    .filter(Boolean)
    .map((code) => {
      const match = code.match(/(\d+)$/);
      return match ? Number(match[1]) : 0;
    });
  const nextSequence = sequences.length > 0 ? Math.max(...sequences) + 1 : 1;
  return `LIC-${year}-${String(nextSequence).padStart(3, '0')}`;
};

const computeNextPurchaseRequisitionId = (currentRequisitions = []) => {
  const sequences = currentRequisitions
    .map((req) => String(req?.id || ''))
    .map((id) => {
      const match = id.match(/^PR-(\d+)$/i);
      return match ? Number(match[1]) : null;
    })
    .filter((value) => Number.isFinite(value));

  const lastSequence = sequences.length > 0 ? Math.max(...sequences) : 3000;
  return `PR-${lastSequence + 1}`;
};

const recalculateOfferScores = (offers = []) => {
  const validAmounts = offers.map((offer) => Number(offer.amount || 0)).filter((amount) => amount > 0);
  const bestAmount = validAmounts.length > 0 ? Math.min(...validAmounts) : 0;
  return offers.map((offer) => {
    const amount = Number(offer.amount || 0);
    const score =
      bestAmount > 0 && amount > 0
        ? Math.round((bestAmount / amount) * 100)
        : offer.score || 0;
    return {
      ...offer,
      score
    };
  });
};

const getPreferredOffer = (tender) => {
  if (!tender?.offers || tender.offers.length === 0) return null;
  if (tender.awardedOfferId) {
    const awarded = tender.offers.find((offer) => offer.id === tender.awardedOfferId);
    if (awarded) return awarded;
  }
  const offersWithAmount = tender.offers.filter(
    (offer) => offer.amount != null && !Number.isNaN(Number(offer.amount))
  );
  if (offersWithAmount.length === 0) {
    return tender.offers[0];
  }
  return offersWithAmount.reduce((best, offer) => {
    if (!best) return offer;
    return Number(offer.amount) < Number(best.amount) ? offer : best;
  }, null);
};

const INITIAL_PROJECT_INCOMES = [
  {
    id: 'INC-LEYDA-01',
    parkId: 'park-leyda',
    title: 'Hito 1 - Cierre financiero',
    milestoneId: 'mil-ren-1',
    expectedDate: '2025-03-15',
    amount: 3500000,
    status: 'pendiente',
    receivedAmount: 0
  },
  {
    id: 'INC-LEYDA-02',
    parkId: 'park-leyda',
    title: 'Hito 2 - EPC 50%',
    milestoneId: 'mil-ren-2',
    expectedDate: '2025-07-15',
    amount: 4200000,
    status: 'pendiente',
    receivedAmount: 0
  },
  {
    id: 'INC-HURTADO-01',
    parkId: 'park-hurtado',
    title: 'Hito prefactibilidad aprobada',
    milestoneId: 'mil-ren-3',
    expectedDate: '2025-05-05',
    amount: 1800000,
    status: 'pendiente',
    receivedAmount: 0
  }
];

const DEFAULT_PREFERENCES = {
  displayCurrency: 'USD',
  fxUsdClp: 950,
  projectContext: {
    parkId: 'all'
  },
  dashboardExecutive: {
    parkId: 'all',
    sections: ['development', 'operations', 'project_management', 'procurement', 'construction', 'cost_control']
  }
};

const sanitizePreferences = (input) => {
  const prefs = input && typeof input === 'object' ? input : {};
  const projectContext = prefs.projectContext && typeof prefs.projectContext === 'object' ? prefs.projectContext : {};
  const dashboardExecutive =
    prefs.dashboardExecutive && typeof prefs.dashboardExecutive === 'object'
      ? prefs.dashboardExecutive
      : {};
  const sections = Array.isArray(dashboardExecutive.sections) ? dashboardExecutive.sections : null;
  const safeSections = sections
    ? sections.filter((item) => typeof item === 'string')
    : DEFAULT_PREFERENCES.dashboardExecutive.sections;
  const fx = Number(prefs.fxUsdClp);

  return {
    displayCurrency: prefs.displayCurrency === 'CLP' ? 'CLP' : 'USD',
    fxUsdClp: Number.isFinite(fx) && fx > 0 ? fx : DEFAULT_PREFERENCES.fxUsdClp,
    projectContext: {
      parkId: typeof projectContext.parkId === 'string' ? projectContext.parkId : 'all'
    },
    dashboardExecutive: {
      parkId: typeof dashboardExecutive.parkId === 'string' ? dashboardExecutive.parkId : 'all',
      sections: safeSections.length > 0 ? safeSections : DEFAULT_PREFERENCES.dashboardExecutive.sections
    }
  };
};

export const AppProvider = ({ children }) => {
  const persistedStateRef = useRef(loadPersistedState());
  const combinedUsers = mergeUsersWithBaseline(initialUsers, persistedStateRef.current?.users);
  const baseUsers = normalizeUsers(combinedUsers);
  const baseTenders = (persistedStateRef.current?.tenders || initialTenders).map((tender) =>
    normalizeTender(tender, baseUsers)
  );
  const [currentUser, setCurrentUser] = useState(() => null);
  const [activePage, setActivePage] = useState(
    () => 'login'
  );
  const [users, setUsers] = useState(() => baseUsers);
  const [inventory, setInventory] = useState(
    () => persistedStateRef.current?.inventory || initialInventory
  );
  const baselineOrders = ensureOrdersLifecycle(ensureHistory(initialPurchaseOrders));
  const persistedOrders = ensureOrdersLifecycle(
    ensureHistory(persistedStateRef.current?.purchaseOrders || [])
  );

  const [purchaseOrders, setPurchaseOrders] = useState(() =>
    mergeCollectionsById(baselineOrders, persistedOrders)
  );
  const [purchaseRequisitions, setPurchaseRequisitions] = useState(
    () => persistedStateRef.current?.purchaseRequisitions || initialPurchaseRequisitions
  );
  const [shipments, setShipments] = useState(
    () => persistedStateRef.current?.shipments || initialShipments
  );
  const [movements, setMovements] = useState(
    () => persistedStateRef.current?.movements || initialMovements
  );
  const [suppliers, setSuppliers] = useState(() =>
    applySuppliersDefaults(persistedStateRef.current?.suppliers || initialSuppliers)
  );
  const [notifications, setNotifications] = useState(
    () => persistedStateRef.current?.notifications || initialNotifications
  );
  const [auditTrail, setAuditTrail] = useState(
    () => persistedStateRef.current?.auditTrail || initialAuditLog
  );
  const [tenders, setTenders] = useState(() => baseTenders);
  const [lawyers, setLawyers] = useState(
    () => persistedStateRef.current?.lawyers || initialLawyers
  );
  const [legalCases, setLegalCases] = useState(
    () => persistedStateRef.current?.legalCases || initialLegalCases
  );
  const [legalContracts, setLegalContracts] = useState(
    () => persistedStateRef.current?.legalContracts || initialLegalContracts
  );
  const [legalCompliance, setLegalCompliance] = useState(
    () => persistedStateRef.current?.legalCompliance || initialLegalCompliance
  );
  const [legalDocuments, setLegalDocuments] = useState(
    () => persistedStateRef.current?.legalDocuments || initialLegalDocuments
  );
  const [legalPowers, setLegalPowers] = useState(
    () => persistedStateRef.current?.legalPowers || initialLegalPowers
  );
  const [legalReports, setLegalReports] = useState(
    () => persistedStateRef.current?.legalReports || initialLegalReports
  );

  const [locations, setLocations] = useState(
    () => persistedStateRef.current?.locations || initialLocations
  );
  const [costCenters, setCostCenters] = useState(
    () => persistedStateRef.current?.costCenters || initialCostCenters
  );
  const [parks, setParks] = useState(() => persistedStateRef.current?.parks || initialParks);
  const [categories, setCategories] = useState(
    () => persistedStateRef.current?.categories || initialCategories
  );
  const [projectBudgets, setProjectBudgets] = useState(
    () => persistedStateRef.current?.projectBudgets || initialProjectBudgets
  );
  const [projectTimelines, setProjectTimelines] = useState(
    () => persistedStateRef.current?.projectTimelines || initialProjectTimelines
  );
  const [projectStatusItems, setProjectStatusItems] = useState(
    () => persistedStateRef.current?.projectStatusItems || initialProjectStatusMatrix
  );
  const [projectStatusProjects, setProjectStatusProjects] = useState(
    () => persistedStateRef.current?.projectStatusProjects || initialProjectStatusProjects
  );
  const [renewableProjects, setRenewableProjects] = useState(
    () => persistedStateRef.current?.renewableProjects || initialRenewableProjects
  );
  const [renewablePermits, setRenewablePermits] = useState(
    () => persistedStateRef.current?.renewablePermits || initialRenewablePermits
  );
  const [renewableRisks, setRenewableRisks] = useState(
    () => persistedStateRef.current?.renewableRisks || initialRenewableRisks
  );
  const [renewableMilestones, setRenewableMilestones] = useState(
    () => persistedStateRef.current?.renewableMilestones || initialRenewableMilestones
  );
  const [projectIncomes, setProjectIncomes] = useState(
    () => persistedStateRef.current?.projectIncomes || INITIAL_PROJECT_INCOMES
  );

  const [rfqs, setRfqs] = useState(() => persistedStateRef.current?.rfqs || initialRfqs);

  const [preferences, setPreferences] = useState(() =>
    sanitizePreferences(persistedStateRef.current?.preferences || DEFAULT_PREFERENCES)
  );

  const updatePreferences = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setPreferences((prev) => {
      const nextProjectContext =
        patch.projectContext && typeof patch.projectContext === 'object'
          ? { ...(prev.projectContext || {}), ...patch.projectContext }
          : prev.projectContext;
      const nextDashboard =
        patch.dashboardExecutive && typeof patch.dashboardExecutive === 'object'
          ? { ...(prev.dashboardExecutive || {}), ...patch.dashboardExecutive }
          : prev.dashboardExecutive;
      const merged = {
        ...prev,
        ...patch,
        projectContext: nextProjectContext,
        dashboardExecutive: nextDashboard
      };
      return sanitizePreferences(merged);
    });
  }, []);

  const projectCatalog = useMemo(() => {
    const map = new Map();
    projectStatusProjects.forEach((project) => map.set(project.id, project));
    return map;
  }, []);

  const projectTimelineMap = useMemo(() => {
    return projectTimelines.reduce((acc, timeline) => {
      acc[timeline.parkId] = timeline;
      return acc;
    }, {});
  }, [projectTimelines]);

  const getProjectTimeline = useCallback(
    (parkId) => projectTimelineMap[parkId] || null,
    [projectTimelineMap]
  );

  const addProjectTimelineTask = useCallback(
    (parkId, taskData) => {
      if (!parkId || !taskData) return null;
      const sanitizedProgress = Math.min(Math.max(Number(taskData.progress) || 0, 0), 100);
      const start = taskData.start || null;
      const end = taskData.end || null;
      if (!start || !end) return null;
      const newTask = {
        id: taskData.id || generateId('GANTT'),
        name: taskData.name || 'Nueva tarea',
        partida: taskData.partida || '',
        subPartida: taskData.subPartida || '',
        start,
        end,
        owner: taskData.owner || '',
        progress: sanitizedProgress
      };

      setProjectTimelines((prev) => {
        const existing = prev.find((timeline) => timeline.parkId === parkId);
        if (!existing) {
          return [
            ...prev,
            {
              parkId,
              baselineStart: start,
              baselineEnd: end,
              tasks: [newTask]
            }
          ];
        }
        return prev.map((timeline) =>
          timeline.parkId === parkId
            ? {
                ...timeline,
                tasks: [...timeline.tasks, newTask],
                baselineStart: timeline.baselineStart || start,
                baselineEnd: timeline.baselineEnd || end
              }
            : timeline
        );
      });

      return newTask;
    },
    []
  );

  useEffect(() => {
    if (!isBrowser) return;
    const dataToPersist = {
      currentUser,
      activePage,
      preferences,
      users,
      locations,
      parks,
      costCenters,
      categories,
      inventory,
      purchaseOrders,
      purchaseRequisitions,
      shipments,
      movements,
      suppliers,
      notifications,
      auditTrail,
      tenders,
      rfqs,
      projectStatusProjects,
      projectStatusItems,
      projectTimelines,
	      projectBudgets,
	      projectIncomes,
	      renewableProjects,
	      renewablePermits,
	      renewableRisks,
	      renewableMilestones,
	      lawyers,
	      legalCases,
	      legalContracts,
      legalCompliance,
      legalDocuments,
      legalPowers,
      legalReports
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToPersist));
    } catch (error) {
      console.warn('No fue posible guardar datos persistidos.', error);
    }
	  }, [
	    currentUser,
	    activePage,
	    preferences,
	    users,
	    locations,
	    parks,
	    costCenters,
	    categories,
    inventory,
    purchaseOrders,
    purchaseRequisitions,
    shipments,
    movements,
    suppliers,
    notifications,
    auditTrail,
    tenders,
    rfqs,
    projectStatusProjects,
    projectStatusItems,
    projectTimelines,
    projectBudgets,
	    projectIncomes,
	    renewableProjects,
	    renewablePermits,
	    renewableRisks,
	    renewableMilestones,
	    lawyers,
	    legalCases,
	    legalContracts,
	    legalCompliance,
	    legalDocuments,
	    legalPowers,
	    legalReports
	  ]);

  const findUserByEmail = (email) => users.find((user) => user.email.toLowerCase() === email.toLowerCase().trim());
  const findUserById = (id) => users.find((user) => user.id === id);
  const findCostCenterById = (id) => costCenters.find((costCenter) => costCenter.id === id);
  const findParkById = (id) => parks.find((park) => park.id === id);
  const findAreaById = (areaId) => {
    for (const park of parks) {
      const area = (park.areas || []).find((candidate) => candidate.id === areaId);
      if (area) {
        return { ...area, parkId: park.id, parkName: park.name };
      }
    }
    return null;
  };

  const categoryBudgetMap = useMemo(() => {
    return categories.reduce((acc, category) => {
      acc[category.id] = category.budgetCategory || 'otros';
      return acc;
    }, {});
  }, [categories]);

  const addAuditRecord = (action, entity, result, userId = currentUser?.id) => {
    setAuditTrail((prev) => [
      {
        id: generateId('AUDIT'),
        date: now(),
        userId,
        action,
        entity,
        result
      },
      ...prev
    ]);
  };

  const pushNotification = ({ title, message, severity = 'low' }) => {
    setNotifications((prev) => [
      {
        id: generateId('NOT'),
        title,
        message,
        severity,
        createdAt: now(),
        read: false
      },
      ...prev
    ]);
  };

  const resetAppToZero = useCallback(() => {
    if (!isBrowser) return;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('No fue posible limpiar el almacenamiento local.', error);
    }

    const baselineAdmin = normalizeUsers(initialUsers)[0] || null;

	    setUsers(baselineAdmin ? [baselineAdmin] : []);
	    setCurrentUser(null);
	    setActivePage('login');
	    setPreferences(DEFAULT_PREFERENCES);

    setParks([]);
    setCostCenters([]);
    setCategories([]);
    setLocations(initialLocations);

    setPurchaseRequisitions([]);
    setPurchaseOrders([]);
    setTenders([]);
    setRfqs([]);
    setSuppliers([]);
    setInventory([]);
    setShipments([]);
    setMovements([]);

    setProjectBudgets([]);
    setProjectIncomes([]);
    setProjectTimelines([]);
    setProjectStatusItems([]);
    setProjectStatusProjects([]);
    setNotifications([]);
    setAuditTrail([]);

    setRenewableProjects([]);
    setRenewablePermits([]);
    setRenewableRisks([]);
    setRenewableMilestones([]);
    setLawyers([]);
    setLegalCases([]);
    setLegalContracts([]);
    setLegalCompliance([]);
    setLegalDocuments([]);
    setLegalPowers([]);
    setLegalReports([]);
  }, []);

  const createRenewableProject = useCallback((payload = {}) => {
    const name = String(payload.name || '').trim();
    if (!name) throw new Error(t('Nombre de proyecto de desarrollo requerido.'));
    const parkId = payload.parkId || null;
    const id = payload.id || `ren-${generateId('REN').toLowerCase()}`;
    const technology = String(payload.technology || 'Solar FV');
    const region = String(payload.region || '').trim();
    const commune = String(payload.commune || '').trim();
    const mw = Number(payload.mw || 0);
    const stage = payload.stage || 'originacion';
    const rtbProgress = Math.max(0, Math.min(100, Number(payload.rtbProgress || 0)));
    const coordinates = payload.coordinates || null;
    const responsibleId = payload.responsibleId || null;

    const newProject = {
      id,
      parkId,
      name,
      technology,
      region,
      commune,
      mw: Number.isFinite(mw) ? mw : 0,
      stage,
      rtbProgress,
      responsibleId,
      approval: payload.approval || { projectManager: false, purchasingManager: false },
      coordinates
    };

    setRenewableProjects((prev) => [newProject, ...(prev || [])]);
    addAuditRecord('Alta proyecto desarrollo', id, name);
    return newProject;
  }, [addAuditRecord]);

  const addRenewablePermit = useCallback((payload = {}) => {
    const projectId = payload.projectId || null;
    if (!projectId) throw new Error(t('projectId requerido para PAS/Permiso.'));
    const id = payload.id || `perm-${generateId('PERM').toLowerCase()}`;
    const name = String(payload.name || '').trim() || 'PAS / Permiso';
    const expectedDate = payload.expectedDate || null;
    const status = payload.status || 'Por solicitar';
    const progress = Math.max(0, Math.min(100, Number(payload.progress || 0)));
    const permit = {
      id,
      projectId,
      name,
      description: payload.description || '',
      type: payload.type || 'Sectorial',
      status,
      requestDate: payload.requestDate || null,
      expectedDate,
      progress,
      responsible: payload.responsible || '—',
      attachments: Array.isArray(payload.attachments) ? payload.attachments : []
    };
    setRenewablePermits((prev) => [permit, ...(prev || [])]);
    addAuditRecord('Alta PAS/Permiso', id, name);
    return permit;
  }, [addAuditRecord]);

  const addDefaultPermitsForRenewableProject = useCallback((renewableProjectId) => {
    if (!renewableProjectId) return;
    const year = new Date().getFullYear();
    const baseline = [
      { name: 'RCA - Evaluación Ambiental (SEA)', type: 'Ambiental', status: 'Por solicitar', progress: 5, expectedDate: `${year + 1}-06-30` },
      { name: 'PAS Eléctrico - Coordinación SEC/CEN', type: 'Sectorial', status: 'Por solicitar', progress: 0, expectedDate: `${year + 1}-09-30` },
      { name: 'Permiso municipal / DOM', type: 'Municipal', status: 'Por solicitar', progress: 0, expectedDate: `${year + 1}-03-31` }
    ];
    baseline.forEach((permit) => {
      addRenewablePermit({ projectId: renewableProjectId, ...permit });
    });
  }, [addRenewablePermit]);

  const createPark = useCallback((payload = {}) => {
    const name = String(payload.name || '').trim();
    if (!name) throw new Error(t('Nombre de proyecto requerido.'));
    const code = String(payload.code || '').trim() || generateId('PRJ');
    const parkId = payload.id || `park-${generateId('PRK').toLowerCase()}`;
    const managerId = payload.managerId || null;
    const createDevelopment = payload.createDevelopment !== false;
    const newPark = {
      id: parkId,
      name,
      code,
      managerId,
      areas: [],
      packages: [],
      team: {
        projectManagerId: managerId,
        administrators: [],
        controllerId: null,
        fieldTeam: [],
        areaManagers: []
      }
    };
    setParks((prev) => [newPark, ...(prev || [])]);
    addAuditRecord('Alta proyecto', parkId, name);

    setProjectBudgets((prev) => {
      const existing = (prev || []).some((budget) => budget.parkId === parkId);
      if (existing) return prev;
      return [
        ...prev,
        {
          parkId,
          currency: payload.budgetCurrency === 'CLP' ? 'CLP' : 'USD',
          total: 0,
          allocations: []
        }
      ];
    });

    if (createDevelopment) {
      const renewableProject = createRenewableProject({
        parkId,
        name,
        technology: payload.technology || 'Solar FV',
        region: payload.region || '',
        commune: payload.commune || '',
        mw: payload.mw || 0,
        stage: payload.stage || 'originacion',
        rtbProgress: payload.rtbProgress || 0,
        coordinates: payload.coordinates || null,
        responsibleId: payload.responsibleId || managerId || null
      });
      addDefaultPermitsForRenewableProject(renewableProject.id);
    }

    // Persiste en backend/core con EL MISMO id que se generó acá arriba
    // (Prisma acepta un id explícito en vez de generar uno nuevo) — así el
    // proyecto local y el de Postgres quedan con el mismo id desde el vamos,
    // sin tener que volver a mapear nada. El presupuesto y los 3 permisos
    // default los crea el backend solo (misma lógica que acá), con sus
    // propios ids — se reconcilian con los locales en el próximo fetch de
    // /projects, no antes.
    projectsApi
      .create({
        id: parkId,
        name,
        code,
        projectManagerId: managerId || undefined,
        technology: createDevelopment ? payload.technology || 'Solar FV' : undefined,
        region: createDevelopment ? payload.region || undefined : undefined,
        commune: createDevelopment ? payload.commune || undefined : undefined,
        capacityMw: createDevelopment ? payload.mw || undefined : undefined,
        latitude: payload.coordinates?.lat,
        longitude: payload.coordinates?.lng
      })
      .catch((error) => {
        console.warn('No fue posible crear el proyecto en backend/core.', error);
      });

    return newPark;
  }, [addAuditRecord, createRenewableProject, addDefaultPermitsForRenewableProject]);

  const setProjectBudgetCurrency = useCallback(
    (parkId, currency) => {
      if (!parkId) throw new Error(t('Proyecto inválido.'));
      const nextCurrency = currency === 'CLP' ? 'CLP' : 'USD';
      setProjectBudgets((prev) => {
        const existing = (prev || []).find((budget) => budget.parkId === parkId) || null;
        if (!existing) {
          return [
            ...prev,
            {
              parkId,
              currency: nextCurrency,
              total: 0,
              allocations: []
            }
          ];
        }
        return (prev || []).map((budget) =>
          budget.parkId === parkId ? { ...budget, currency: nextCurrency } : budget
        );
      });
      addAuditRecord('Moneda presupuesto proyecto', parkId, nextCurrency);
      return nextCurrency;
    },
    [addAuditRecord]
  );

  const addParkPackage = useCallback((parkId, payload = {}) => {
    if (!parkId) throw new Error(t('Proyecto inválido.'));
    const name = String(payload.name || '').trim();
    if (!name) throw new Error(t('Nombre de paquete requerido.'));
    const code = String(payload.code || '').trim() || generateId('PKG');
    const packageId = payload.id || `pkg-${generateId('PKG').toLowerCase()}`;

    setParks((prev) =>
      (prev || []).map((park) => {
        if (park.id !== parkId) return park;
        const packages = Array.isArray(park.packages) ? park.packages : [];
        return {
          ...park,
          packages: [...packages, { id: packageId, code, name }]
        };
      })
    );

    addAuditRecord('Alta paquete WBS', `${parkId}:${packageId}`, `${code} ${name}`);
    projectsApi.addPackage(parkId, { id: packageId, code, name }).catch((error) => {
      console.warn('No fue posible sincronizar el paquete WBS con backend/core.', error);
    });
    return { id: packageId, code, name };
  }, [addAuditRecord]);

  const addParkArea = useCallback((parkId, payload = {}) => {
    if (!parkId) throw new Error(t('Proyecto inválido.'));
    const name = String(payload.name || '').trim();
    if (!name) throw new Error(t('Nombre de área requerido.'));
    const areaId = payload.id || `area-${generateId('AREA').toLowerCase()}`;
    const areaManagerId = payload.areaManagerId || null;

    const costCenterCode = String(payload.costCenterCode || '').trim() || `CC-${generateId('CC')}`;
    const costCenterName = String(payload.costCenterName || '').trim() || `${name} (${parkId})`;
    const costCenterDescription = String(payload.costCenterDescription || '').trim();
    const costCenterId = payload.costCenterId || `cc-${generateId('CC').toLowerCase()}`;

    setCostCenters((prev) => [
      {
        id: costCenterId,
        code: costCenterCode,
        name: costCenterName,
        parkId,
        areaId,
        description: costCenterDescription
      },
      ...(prev || [])
    ]);

    setParks((prev) =>
      (prev || []).map((park) => {
        if (park.id !== parkId) return park;
        const nextAreas = [...(park.areas || []), { id: areaId, name, costCenterId, areaManagerId }];
        const nextAreaManagers = Array.from(
          new Set([...(park.team?.areaManagers || []), ...(areaManagerId ? [areaManagerId] : [])])
        );
        return {
          ...park,
          areas: nextAreas,
          team: {
            ...(park.team || {}),
            areaManagers: nextAreaManagers
          }
        };
      })
    );

    addAuditRecord('Alta área', `${parkId}:${areaId}`, name);
    projectsApi
      .addArea(parkId, {
        id: areaId,
        name,
        areaManagerId: areaManagerId || undefined,
        costCenterId,
        costCenterCode,
        costCenterName,
        costCenterDescription: costCenterDescription || undefined
      })
      .catch((error) => {
        console.warn('No fue posible sincronizar el área con backend/core.', error);
      });
    return { id: areaId, name, costCenterId, areaManagerId };
  }, [addAuditRecord]);

  const updatePark = useCallback((parkId, payload = {}) => {
    if (!parkId) throw new Error(t('Proyecto inválido.'));

    const nextName = payload.name !== undefined ? String(payload.name || '').trim() : undefined;
    const nextCode = payload.code !== undefined ? String(payload.code || '').trim() : undefined;
    const nextManagerId = payload.managerId !== undefined ? (payload.managerId || null) : undefined;

    const nextCoordinates =
      payload.coordinates !== undefined
        ? payload.coordinates &&
          Number.isFinite(Number(payload.coordinates.lat)) &&
          Number.isFinite(Number(payload.coordinates.lng))
          ? { lat: Number(payload.coordinates.lat), lng: Number(payload.coordinates.lng) }
          : null
        : undefined;

    setParks((prev) =>
      (prev || []).map((park) => {
        if (park.id !== parkId) return park;
        const previousTeam = park.team || {};
        const patchedManagerId = nextManagerId !== undefined ? nextManagerId : park.managerId || null;
        return {
          ...park,
          name: nextName !== undefined && nextName ? nextName : park.name,
          code: nextCode !== undefined && nextCode ? nextCode : park.code,
          managerId: patchedManagerId,
          team: {
            ...previousTeam,
            projectManagerId: patchedManagerId || previousTeam.projectManagerId || null
          }
        };
      })
    );

    setRenewableProjects((prev) =>
      (prev || []).map((project) => {
        if (project.parkId !== parkId) return project;
        return {
          ...project,
          name: nextName !== undefined && nextName ? nextName : project.name,
          technology:
            payload.technology !== undefined
              ? String(payload.technology || project.technology || 'Solar FV')
              : project.technology,
          region: payload.region !== undefined ? String(payload.region || '') : project.region,
          commune: payload.commune !== undefined ? String(payload.commune || '') : project.commune,
          mw: payload.mw !== undefined ? Number(payload.mw || 0) : project.mw,
          coordinates: nextCoordinates !== undefined ? nextCoordinates : project.coordinates,
          responsibleId: nextManagerId !== undefined ? nextManagerId : project.responsibleId
        };
      })
    );

    addAuditRecord('Edición proyecto', parkId, nextName || 'Actualización');
    return true;
  }, [addAuditRecord]);

  const findPackageById = useCallback(
    (parkId, packageId) => {
      const park = parkId ? findParkById(parkId) : null;
      if (!park || !packageId) return null;
      return (park.packages || []).find((pkg) => pkg.id === packageId) || null;
    },
    [findParkById]
  );

  const createCategory = useCallback((payload = {}) => {
    const name = String(payload.name || '').trim();
    if (!name) throw new Error(t('Nombre de categoría requerido.'));
    const id = payload.id || `cat-${generateId('CAT').toLowerCase()}`;
    const type = payload.type === 'servicio' ? 'servicio' : 'material';
    const budgetCategory = String(payload.budgetCategory || '').trim() || 'otros';
    const newCategory = { id, name, type, budgetCategory };
    setCategories((prev) => [newCategory, ...(prev || [])]);
    addAuditRecord('Alta categoría', id, budgetCategory);
    return newCategory;
  }, [addAuditRecord]);

  // Carga datos base de un seed (p. ej. el demo de Construcción) en las
  // colecciones existentes, combinando por id para no duplicar si se carga dos veces.
  const importSeedData = useCallback((seed = {}) => {
    const merge = (setter, list) => {
      if (Array.isArray(list) && list.length > 0) setter((prev) => mergeCollectionsById(prev || [], list));
    };
    merge(setParks, seed.parks);
    merge(setCostCenters, seed.costCenters);
    merge(setCategories, seed.categories);
    merge(setProjectBudgets, seed.projectBudgets);
    merge(setRenewableProjects, seed.renewableProjects);
    merge(setSuppliers, (seed.suppliers || []).map(applySupplierDefaults));
    merge(setLocations, seed.locations);
    merge(setInventory, seed.inventory);
    merge(setPurchaseOrders, seed.purchaseOrders);
    merge(setShipments, seed.shipments);
    merge(setMovements, seed.movements);
    addAuditRecord('Carga de datos demo', seed.label || 'seed', 'OK');
  }, [addAuditRecord]);

  const loadEnergyTemplates = useCallback(() => {
    setCategories((prev) => mergeCollectionsById(prev || [], energyTemplates.categories || []));
    setParks((prev) => mergeCollectionsById(prev || [], energyTemplates.parks || []));
    setCostCenters((prev) => mergeCollectionsById(prev || [], energyTemplates.costCenters || []));
    setProjectBudgets((prev) => mergeCollectionsById(prev || [], energyTemplates.projectBudgets || []));

    setRenewableProjects((prev) => mergeCollectionsById(prev || [], energyTemplates.renewableProjects || []));
    setRenewablePermits((prev) => mergeCollectionsById(prev || [], energyTemplates.renewablePermits || []));
    setRenewableRisks((prev) => mergeCollectionsById(prev || [], energyTemplates.renewableRisks || []));
    setRenewableMilestones((prev) =>
      mergeCollectionsById(prev || [], energyTemplates.renewableMilestones || [])
    );

    setProjectStatusProjects(() =>
      Array.isArray(energyTemplates.projectStatusProjects) ? energyTemplates.projectStatusProjects : []
    );
    setProjectStatusItems((prev) => mergeCollectionsById(prev || [], energyTemplates.projectStatusMatrix || []));

    pushNotification({
      title: t('Templates energía cargados'),
      message: t('Se cargaron proyectos, categorías, presupuestos y controles base (demo).'),
      severity: 'info'
    });
  }, [pushNotification]);

  const stageOrderIndex = useMemo(() => {
    const map = new Map();
    projectStageOrder.forEach((stage, index) => map.set(stage, index));
    return map;
  }, []);

  const enhancedProjectStatusItems = useMemo(() => {
    return projectStatusItems.map((item) => {
      const statusLabel = normalizeStatusLabel(item.status);
      const progressWeight = normalizeStatusWeight(item.status);
      const controlType = classifyProjectControlType(item);
      const semaphore = item.semaphore || resolveSemaphore(item.status, item.expectedDate);
      return {
        ...item,
        statusLabel,
        progressWeight,
        controlType,
        semaphore,
        requiresPermit: controlType === 'permiso',
        isContract: controlType === 'contrato',
        isStudy: controlType === 'estudio',
        isCompleted: progressWeight >= 100,
        isInProgress: progressWeight > 0 && progressWeight < 100
      };
    });
  }, [projectStatusItems]);

  const projectStatusProjectMap = useMemo(() => {
    const createWorkingSummary = () => ({
      ...createEmptySummary(),
      _progressSum: 0
    });

    const accumulate = (summary, item) => {
      summary.total += 1;
      if (item.requiresPermit) summary.permits += 1;
      if (item.isContract) summary.contracts += 1;
      if (item.isStudy) summary.studies += 1;
      if (item.isCompleted) {
        summary.completed += 1;
      } else if (item.isInProgress) {
        summary.inProgress += 1;
      } else {
        summary.pending += 1;
      }
      if (item.semaphore === 'critical') {
        summary.critical += 1;
      } else if (item.semaphore === 'success' || item.semaphore === 'progress') {
        summary.onTrack += 1;
      }
      summary._progressSum += item.progressWeight;
    };

    const finalizeSummary = (summary) => {
      if (summary.total > 0) {
        summary.overallProgress = Math.round(summary._progressSum / summary.total);
      } else {
        summary.overallProgress = 0;
      }
      delete summary._progressSum;
      return summary;
    };

    const ensureStageGroup = (entry, stage) => {
      if (!entry.stageMap.has(stage)) {
        entry.stageMap.set(stage, {
          stage,
          items: [],
          summary: createWorkingSummary()
        });
      }
      return entry.stageMap.get(stage);
    };

    const ensureCategoryGroup = (entry, category) => {
      if (!entry.categoryMap.has(category)) {
        entry.categoryMap.set(category, {
          name: category,
          items: [],
          summary: createWorkingSummary(),
          stageMap: new Map()
        });
      }
      return entry.categoryMap.get(category);
    };

    const ensureCategoryStage = (categoryGroup, stage) => {
      if (!categoryGroup.stageMap.has(stage)) {
        categoryGroup.stageMap.set(stage, {
          stage,
          items: [],
          summary: createWorkingSummary()
        });
      }
      return categoryGroup.stageMap.get(stage);
    };

    const map = new Map();
    const masterKey = '__all__';

    const ensureProject = (projectId) => {
      if (!map.has(projectId)) {
        map.set(projectId, {
          projectId: projectId === masterKey ? masterKey : projectId,
          items: [],
          summary: createWorkingSummary(),
          stageMap: new Map(),
          categoryMap: new Map()
        });
      }
      return map.get(projectId);
    };

    enhancedProjectStatusItems.forEach((item) => {
      const projectId = item.projectId || masterKey;
      const projectEntry = ensureProject(projectId);
      const globalEntry = ensureProject(masterKey);

      [projectEntry, globalEntry].forEach((entry) => {
        entry.items.push(item);
        accumulate(entry.summary, item);

        const stageGroup = ensureStageGroup(entry, item.stage);
        stageGroup.items.push(item);
        accumulate(stageGroup.summary, item);

        const categoryGroup = ensureCategoryGroup(entry, item.category);
        categoryGroup.items.push(item);
        accumulate(categoryGroup.summary, item);

        const categoryStageGroup = ensureCategoryStage(categoryGroup, item.stage);
        categoryStageGroup.items.push(item);
        accumulate(categoryStageGroup.summary, item);
      });
    });

    map.forEach((entry, key) => {
      entry.summary = finalizeSummary(entry.summary);
      entry.project = key === masterKey ? null : projectCatalog.get(entry.projectId) || null;

      const stages = Array.from(entry.stageMap.values()).map((stageEntry) => ({
        stage: stageEntry.stage,
        summary: finalizeSummary(stageEntry.summary),
        items: stageEntry.items
      }));

      stages.sort((a, b) => {
        const aIndex = stageOrderIndex.has(a.stage) ? stageOrderIndex.get(a.stage) : Number.MAX_SAFE_INTEGER;
        const bIndex = stageOrderIndex.has(b.stage) ? stageOrderIndex.get(b.stage) : Number.MAX_SAFE_INTEGER;
        return aIndex === bIndex ? a.stage.localeCompare(b.stage) : aIndex - bIndex;
      });

      entry.stages = stages;

      const categories = Array.from(entry.categoryMap.values()).map((categoryEntry) => {
        const stageList = Array.from(categoryEntry.stageMap.values()).map((categoryStageEntry) => ({
          stage: categoryStageEntry.stage,
          summary: finalizeSummary(categoryStageEntry.summary),
          items: categoryStageEntry.items
        }));

        stageList.sort((a, b) => {
          const aIndex = stageOrderIndex.has(a.stage) ? stageOrderIndex.get(a.stage) : Number.MAX_SAFE_INTEGER;
          const bIndex = stageOrderIndex.has(b.stage) ? stageOrderIndex.get(b.stage) : Number.MAX_SAFE_INTEGER;
          return aIndex === bIndex ? a.stage.localeCompare(b.stage) : aIndex - bIndex;
        });

        return {
          name: categoryEntry.name,
          summary: finalizeSummary(categoryEntry.summary),
          items: categoryEntry.items,
          stages: stageList
        };
      });

      categories.sort((a, b) => a.name.localeCompare(b.name));

      entry.categories = categories;

      delete entry.stageMap;
      delete entry.categoryMap;
    });

    return map;
  }, [enhancedProjectStatusItems, projectCatalog, stageOrderIndex]);

  const emptyProjectBundle = useMemo(
    () => ({
      projectId: null,
      project: null,
      items: [],
      summary: createEmptySummary(),
      stages: [],
      categories: []
    }),
    []
  );

  const defaultProjectId = projectStatusProjects[0]?.id || null;

  const getProjectStatusBundle = useCallback(
    (projectId) => {
      const targetId = projectId || defaultProjectId || '__all__';
      return (
        projectStatusProjectMap.get(targetId) ||
        (targetId !== '__all__' ? projectStatusProjectMap.get('__all__') : null) ||
        emptyProjectBundle
      );
    },
    [defaultProjectId, projectStatusProjectMap, emptyProjectBundle]
  );

  const allProjectsStatus = useMemo(
    () => projectStatusProjectMap.get('__all__') || emptyProjectBundle,
    [projectStatusProjectMap, emptyProjectBundle]
  );

  const updateProjectStatusItem = useCallback(
    (itemId, updates) => {
      if (!itemId || !updates) return null;
      let updatedItem = null;
      setProjectStatusItems((prev) =>
        prev.map((item) => {
          if (item.id !== itemId) return item;
          const nextStatus = updates.status || item.status;
          const nextExpectedDate =
            updates.expectedDate !== undefined ? updates.expectedDate : item.expectedDate;
          updatedItem = {
            ...item,
            ...updates,
            status: nextStatus,
            expectedDate: nextExpectedDate,
            semaphore: updates.semaphore || resolveSemaphore(nextStatus, nextExpectedDate)
          };
          return updatedItem;
        })
      );
      if (updatedItem) {
        addAuditRecord(
          'Actualización status proyecto',
          `PRJ-${itemId}`,
          updates.status ? normalizeStatusLabel(updatedItem.status) : 'Actualizado'
        );
      }
      return updatedItem;
    },
    [addAuditRecord]
  );

  const resetProjectStatusMatrix = useCallback(() => {
    setProjectStatusItems(initialProjectStatusMatrix.map((item) => ({ ...item })));
    addAuditRecord('Reset status proyecto', 'Matriz permisos', 'Restablecida');
  }, [addAuditRecord]);

  const renewableProjectMap = useMemo(() => {
    return renewableProjects.reduce((acc, project) => {
      acc[project.id] = project;
      return acc;
    }, {});
  }, [renewableProjects]);

  const renewableProcurementSummary = useMemo(() => {
    const summaryByProject = {};

    renewableProjects.forEach((project) => {
      summaryByProject[project.id] = {
        requisitionsOpen: 0,
        requisitionsConverted: 0,
        tendersOpen: 0,
        purchaseOrdersPendingApproval: 0,
        purchaseOrdersApproved: 0
      };
    });

    purchaseRequisitions.forEach((req) => {
      const parkId = req.parkId || (req.areaId ? findAreaById(req.areaId)?.parkId : null);
      const project = renewableProjects.find((candidate) => candidate.parkId === parkId);
      if (!project || !summaryByProject[project.id]) return;
      if (['pendiente_area', 'pendiente_controller', 'pendiente_compra', 'en_licitacion'].includes(req.status)) {
        summaryByProject[project.id].requisitionsOpen += 1;
      }
      if (req.status === 'convertida') {
        summaryByProject[project.id].requisitionsConverted += 1;
      }
    });

    tenders.forEach((tender) => {
      const parkId = tender.parkId || (tender.requisitionId
        ? purchaseRequisitions.find((req) => req.id === tender.requisitionId)?.parkId
        : null);
      const project = renewableProjects.find((candidate) => candidate.parkId === parkId);
      if (!project || !summaryByProject[project.id]) return;
      if (!['adjudicada', 'cancelada'].includes(tender.status)) {
        summaryByProject[project.id].tendersOpen += 1;
      }
    });

    purchaseOrders.forEach((order) => {
      const parkId = order.parkId || (order.areaId ? findAreaById(order.areaId)?.parkId : null);
      const project = renewableProjects.find((candidate) => candidate.parkId === parkId);
      if (!project || !summaryByProject[project.id]) return;
      if (['pendiente_pm', 'pendiente_compras'].includes(order.status)) {
        summaryByProject[project.id].purchaseOrdersPendingApproval += 1;
      }
      if (['aprobada', 'recibida', 'enviada'].includes(order.status)) {
        summaryByProject[project.id].purchaseOrdersApproved += 1;
      }
    });

    return summaryByProject;
  }, [renewableProjects, purchaseRequisitions, purchaseOrders, tenders, findAreaById]);

  const renewablePermitAlerts = useMemo(() => {
    const today = new Date();
    return renewablePermits
      .map((permit) => {
        const expectedDate = permit.expectedDate ? new Date(`${permit.expectedDate}T00:00:00`) : null;
        const dayDiff = expectedDate
          ? Math.floor((today.getTime() - expectedDate.getTime()) / (1000 * 60 * 60 * 24))
          : null;
        const priority =
          dayDiff != null && dayDiff > 0 ? 'alta' : permit.progress >= 60 ? 'baja' : 'media';
        return {
          id: permit.id,
          projectId: permit.projectId,
          title: permit.name,
          description: permit.description,
          responsible: permit.responsible,
          dueDate: permit.expectedDate,
          overdueDays: dayDiff != null && dayDiff > 0 ? dayDiff : 0,
          priority
        };
      })
      .sort((a, b) => b.overdueDays - a.overdueDays);
  }, [renewablePermits]);

  const renewableKpis = useMemo(() => {
    const projectCount = renewableProjects.length;
    const totalMw = renewableProjects.reduce((sum, project) => sum + Number(project.mw || 0), 0);
    const avgRtb = projectCount > 0
      ? Math.round(renewableProjects.reduce((sum, project) => sum + Number(project.rtbProgress || 0), 0) / projectCount)
      : 0;
    const permitsApproved = renewablePermits.filter((permit) => permit.status === 'Aprobado').length;
    return {
      projectCount,
      totalMw,
      avgRtb,
      permitsLoaded: renewablePermits.length,
      permitsApproved,
      criticalAlerts: renewablePermitAlerts.filter((alert) => alert.priority === 'alta').length
    };
  }, [renewableProjects, renewablePermits, renewablePermitAlerts]);

  const renewablePortfolio = useMemo(() => {
    return renewableProjects.map((project) => {
      const responsible = project.responsibleId ? findUserById(project.responsibleId) : null;
      return {
        ...project,
        stageLabel: renewableStageLabels[project.stage] || project.stage,
        responsibleName: responsible?.name || project.responsible || 'Sin asignar',
        approvalsCompleted: Number(Boolean(project.approval?.projectManager)) + Number(Boolean(project.approval?.purchasingManager)),
        procurement: renewableProcurementSummary[project.id] || {}
      };
    });
  }, [renewableProjects, findUserById, renewableProcurementSummary]);

  const renewableStageIndex = useMemo(() => {
    return renewableStages.reduce((acc, stage, index) => {
      acc[stage] = index;
      return acc;
    }, {});
  }, []);

  const getRenewableProjectBlockers = useCallback(
    (projectId) => {
      const project = renewableProjects.find((item) => item.id === projectId);
      if (!project) return [];
      const blockers = [];
      const approvals = project.approval || {};
      if (!approvals.projectManager || !approvals.purchasingManager) {
        blockers.push('Faltan aprobaciones duales (Gerente Proyecto y Gerente Compras).');
      }

      const hasCriticalPermit = renewablePermitAlerts.some(
        (alert) => alert.projectId === projectId && alert.priority === 'alta'
      );
      if (hasCriticalPermit) {
        blockers.push('Existen permisos/PAS críticos sin regularizar.');
      }

      const procurement = renewableProcurementSummary[projectId];
      if (procurement && procurement.purchaseOrdersPendingApproval > 0) {
        blockers.push('Hay órdenes de compra pendientes de aprobación para el proyecto.');
      }

      return blockers;
    },
    [renewableProjects, renewablePermitAlerts, renewableProcurementSummary]
  );

  const updateRenewableProjectStage = useCallback(
    (projectId, stage, note = '') => {
      if (!projectId || !stage) return;
      const current = renewableProjects.find((item) => item.id === projectId);
      if (!current) return;
      const currentIndex = renewableStageIndex[current.stage];
      const nextIndex = renewableStageIndex[stage];
      const isForwardMove = nextIndex != null && currentIndex != null && nextIndex > currentIndex;
      if (isForwardMove) {
        const blockers = getRenewableProjectBlockers(projectId);
        if (blockers.length > 0) {
          pushNotification({
            title: t('Avance de etapa bloqueado'),
            message: blockers.join(' '),
            severity: 'high'
          });
          return;
        }
      }
      setRenewableProjects((prev) =>
        prev.map((project) =>
          project.id === projectId
            ? {
                ...project,
                stage
              }
            : project
        )
      );
      addAuditRecord('Cambio stage renovable', projectId, renewableStageLabels[stage] || stage);
      if (note) {
        pushNotification({
          title: t('Stage actualizado'),
          message: note,
          severity: 'info'
        });
      }
      // El backend revalida el mismo bloqueo (gate dual + permisos críticos +
      // OCs pendientes) con datos reales de Postgres — si difiere de la
      // validación local (por datos desactualizados), solo se loguea, no se
      // revierte la UI para no romper el flujo ya en curso.
      projectsApi.updateStage(projectId, stage.toUpperCase()).catch((error) => {
        console.warn('No fue posible sincronizar el stage con backend/core.', error);
      });
    },
    [
      renewableProjects,
      renewableStageIndex,
      getRenewableProjectBlockers,
      addAuditRecord,
      pushNotification
    ]
  );

  const updateRenewablePermit = useCallback(
    (permitId, updates = {}) => {
      if (!permitId) return null;
      let updatedPermit = null;
      setRenewablePermits((prev) =>
        prev.map((permit) => {
          if (permit.id !== permitId) return permit;
          updatedPermit = {
            ...permit,
            ...updates
          };
          return updatedPermit;
        })
      );
      if (updatedPermit) {
        addAuditRecord('Actualizacion permiso renovable', permitId, updates.status || 'Actualizado');
      }
      return updatedPermit;
    },
    [addAuditRecord]
  );

  const updateRenewableProjectApproval = useCallback(
    (projectId, gateKey, approved = false) => {
      if (!projectId || !gateKey) return;
      setRenewableProjects((prev) =>
        prev.map((project) => {
          if (project.id !== projectId) return project;
          return {
            ...project,
            approval: {
              ...project.approval,
              [gateKey]: Boolean(approved)
            }
          };
        })
      );
      addAuditRecord(
        'Aprobacion gate renovable',
        `${projectId}-${gateKey}`,
        approved ? 'Aprobado' : 'Pendiente'
      );
      // Persiste en backend/core (el gate real que bloquea Compras ahí vive).
      // `projectId` acá siempre viene de un Project ya cargado desde el backend
      // (mapProjectsResponse reusa el mismo id), así que es seguro sincronizar.
      const gate = gateKey === 'purchasingManager' ? 'purchasing-manager' : 'project-manager';
      projectsApi.decideApproval(projectId, gate, Boolean(approved)).catch((error) => {
        console.warn('No fue posible sincronizar la aprobación con backend/core.', error);
      });
    },
    [addAuditRecord]
  );

  const updateRenewableRisk = useCallback(
    (riskId, updates = {}) => {
      if (!riskId) return;
      setRenewableRisks((prev) =>
        prev.map((risk) =>
          risk.id === riskId
            ? {
                ...risk,
                ...updates
              }
            : risk
        )
      );
      addAuditRecord('Actualizacion riesgo renovable', riskId, updates.status || 'Actualizado');
    },
    [addAuditRecord]
  );

  // login/logout ya NO comparan contra el array `users` en memoria: pegan contra
  // el backend real (backend/core, NestJS + Prisma + bcrypt + JWT). El resto del
  // Context (parks, purchaseOrders, etc.) todavía es local — se migra módulo por
  // módulo. La auditoría de login/logout ahora la registra el propio backend
  // (AuditService), no `addAuditRecord` local.
  // Sin REACT_APP_CORE_API_URL (demo en Netlify / dev sin backend) se valida
  // contra los usuarios demo en memoria.
  const loginLocal = async (email, password) => {
    const normalizedEmail = (email || '').trim().toLowerCase();
    const user = users.find((candidate) => candidate.email?.toLowerCase() === normalizedEmail);
    if (!user || user.password !== password) {
      throw new Error(t('Credenciales inválidas, verifica tu email y contraseña.'));
    }
    return { user, landingPage: 'dashboard' };
  };

  const login = async (email, password) => {
    try {
      const result = process.env.REACT_APP_CORE_API_URL
        ? await coreAuth.login(email, password)
        : await loginLocal(email, password);
      setCurrentUser(result.user);
      const landingPage = result.landingPage || 'dashboard';
      setActivePage(landingPage);
      pushNotification({
        title: t('Bienvenido'),
        message: t(
          'Hola {0}, {1}',
          result.user.name,
          t(ROLE_WELCOME_HINT[result.user.role] || 'tienes acceso al Centro ERP de Compras y Finanzas.')
        ),
        severity: 'info'
      });
      return { success: true, landingPage };
    } catch (error) {
      return {
        success: false,
        message: error.message || t('Credenciales inválidas, verifica tu email y contraseña.')
      };
    }
  };

  const logout = () => {
    if (process.env.REACT_APP_CORE_API_URL) coreAuth.logout().catch(() => {});
    setCurrentUser(null);
    setActivePage('login');
  };

  // Al iniciar sesión, trae los Proyectos reales desde backend/core (Postgres)
  // y los mapea a las formas viejas (parks/renewableProjects/costCenters/...)
  // que el resto del Context y los componentes todavía esperan. Las funciones
  // de escritura (createPark, addParkArea, etc.) siguen actualizando el
  // estado local igual que antes — acá solo se agrega la carga inicial real.
  useEffect(() => {
    if (!currentUser || !process.env.REACT_APP_CORE_API_URL) return;
    let cancelled = false;
    projectsApi
      .list()
      .then((projects) => {
        if (cancelled) return;
        const mapped = mapProjectsResponse(projects);
        setParks(mapped.parks);
        setRenewableProjects(mapped.renewableProjects);
        setCostCenters(mapped.costCenters);
        setProjectBudgets(mapped.projectBudgets);
        setProjectTimelines(mapped.projectTimelines);
        setRenewablePermits(mapped.renewablePermits);
        setRenewableRisks(mapped.renewableRisks);
        setRenewableMilestones(mapped.renewableMilestones);
      })
      .catch((error) => {
        console.warn('No fue posible cargar proyectos desde backend/core, se mantienen los locales.', error);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  const upsertInventoryLocation = (itemLocations, locationId, delta) => {
    const existing = itemLocations.find((loc) => loc.locationId === locationId);
    if (existing) {
      existing.quantity = Math.max(existing.quantity + delta, 0);
    } else {
      itemLocations.push({ locationId, quantity: Math.max(delta, 0) });
    }
    return itemLocations;
  };

  const registerMovement = ({ type, sku, quantity, locationId, reference, userId, parkId, areaId, costCenterId }) => {
    setMovements((prev) => [
      ...prev,
      {
        id: generateId('MOV'),
        type,
        sku,
        quantity,
        locationId,
        parkId: parkId || null,
        areaId: areaId || null,
        costCenterId: costCenterId || null,
        date: now(),
        reference,
        userId: userId || currentUser?.id
      }
    ]);
  };

  const adjustInventory = (sku, locationId, deltaQuantity) => {
    setInventory((prev) =>
      prev.map((item) => {
        if (item.sku !== sku) return item;
        const locationsCopy = [...item.locations.map((loc) => ({ ...loc }))];
        const updatedLocations = upsertInventoryLocation(locationsCopy, locationId, deltaQuantity);
        return {
          ...item,
          locations: updatedLocations,
          lastMovementAt: now()
        };
      })
    );
  };

  const canEditOrder = (role) =>
    ['comprador', 'project_manager', 'purchasing_manager', 'super_user'].includes(role);

  const getBudgetCategoryKeyFromOrder = useCallback(
    (orderData = {}) => {
      if (orderData.categoryId) {
        const category = categories.find((candidate) => candidate.id === orderData.categoryId);
        if (category?.budgetCategory) return category.budgetCategory;
      }
      if (orderData.categoryName) {
        const category = categories.find((candidate) => candidate.name === orderData.categoryName);
        if (category?.budgetCategory) return category.budgetCategory;
      }
      return 'otros';
    },
    [categories]
  );

  const validateOrderBusinessRules = useCallback(
    (orderData = {}, { allowBudgetOverrun = false } = {}) => {
      const errors = [];
      let budgetOverrun = null;
      const supplier = suppliers.find((item) => item.id === orderData.supplierId);
      if (!supplier) {
        errors.push('Proveedor no válido.');
      } else if (
        (supplier.status && supplier.status !== 'Approved') ||
        (!supplier.status && Number(supplier.complianceScore || 0) < 70)
      ) {
        errors.push('El proveedor no está aprobado en compliance.');
      }

      if (!orderData.parkId) {
        errors.push('Debes asignar parque/proyecto para vincular el gasto.');
      }
      if (!orderData.categoryId && !orderData.categoryName) {
        errors.push('Debes asignar categoría presupuestaria para la OC.');
      }

      if (orderData.parkId) {
        const budget = projectBudgets.find((item) => item.parkId === orderData.parkId);
        if (!budget) {
          errors.push('El proyecto no tiene presupuesto configurado.');
        } else {
          const categoryKey = getBudgetCategoryKeyFromOrder(orderData);
          const allocation = budget.allocations.find((item) => item.category === categoryKey);
          if (!allocation) {
            errors.push('La categoría seleccionada no existe en el presupuesto del proyecto.');
          } else {
            const alreadyCommitted = purchaseOrders
              .filter((order) => order.parkId === orderData.parkId && isOrderCommitted(order))
              .filter((order) => order.id !== orderData.id)
              .filter((order) => {
                const orderCategory = getBudgetCategoryKeyFromOrder(order);
                return orderCategory === categoryKey;
              })
              .reduce((sum, order) => sum + Number(order.totalValue || 0), 0);

            const orderValue = Number(orderData.totalValue || 0);
            const available = Math.max(Number(allocation.amount || 0) - alreadyCommitted, 0);
            const projectedCommitted = alreadyCommitted + orderValue;
            if (projectedCommitted > Number(allocation.amount || 0)) {
              budgetOverrun = {
                allocationId: allocation.id,
                allocationName: allocation.name,
                available,
                allocationAvailable: available,
                projectedCommitted,
                delta: projectedCommitted - Number(allocation.amount || 0),
                overrunAmount: projectedCommitted - Number(allocation.amount || 0)
              };
              if (!allowBudgetOverrun) {
                errors.push(
                  `La OC excede presupuesto de la partida ${allocation.name}. Disponible: ${Math.max(
                    Number(allocation.amount || 0) - alreadyCommitted,
                    0
                  ).toLocaleString('es-CL')}.`
                );
              }
            }
          }
        }
      }

      return { errors, budgetOverrun };
    },
    [suppliers, projectBudgets, purchaseOrders, getBudgetCategoryKeyFromOrder]
  );

	  const validateRequisitionBusinessRules = useCallback(
	    (requisition = {}, { allowBudgetOverrun = false } = {}) => {
	      const errors = [];
	      let budgetOverrun = null;

	      if (!requisition.parkId) {
	        errors.push('Debes asignar parque/proyecto para vincular el gasto (PR sin proyecto no puede enviarse).');
	      }
	      if (!requisition.costCenterId) {
	        errors.push('Debes asignar centro de costo (CECO) al proyecto/área antes de enviar.');
	      }
	      if (!requisition.categoryId) {
	        errors.push('Debes asignar categoría presupuestaria antes de enviar.');
	      }
	      if (!requisition.packageId) {
	        errors.push('Debes asignar paquete/WBS al proyecto antes de enviar.');
	      }

	      if (requisition.parkId) {
	        const park = parks.find((item) => item.id === requisition.parkId) || null;
	        const pmId = park?.team?.projectManagerId || park?.managerId || null;
	        if (!pmId) {
	          errors.push(
	            'No puedes enviar PR sin Project Manager asignado al proyecto. Asigna PM en Administración > Parques.'
	          );
	        }
	      }

	      const areaApproverId = requisition.approvals?.areaManager?.userId || null;
	      if (!areaApproverId) {
	        errors.push(
	          'No hay Gerente de Área asignado para aprobación. Asigna Gerente de Área en Administración > Parques > Áreas.'
	        );
	      }

	      const controllerApproverId = requisition.approvals?.purchaseController?.userId || null;
	      if (!controllerApproverId) {
	        errors.push(
	          'No hay Purchase Controller asignado. Asigna Controller al equipo del parque (Administración > Parques) o crea un usuario con rol purchase_controller.'
	        );
	      }

	      const estimate = Number(requisition.estimatedTotal || 0);
	      const safeEstimate = Number.isFinite(estimate) ? Math.max(estimate, 0) : 0;
	      if (safeEstimate <= 0) {
	        errors.push(
	          'Debes ingresar un monto estimado (cantidad y precio unitario) para la requisición. Sin monto no se puede controlar presupuesto.'
	        );
	      }

	      const budget = requisition.parkId
	        ? projectBudgets.find((item) => item.parkId === requisition.parkId)
	        : null;

	      if (requisition.parkId && !budget) {
	        errors.push(
	          'El proyecto no tiene presupuesto configurado. Carga presupuesto en Proyectos > Presupuesto antes de enviar PR.'
	        );
	      }

      if (budget && requisition.categoryId) {
        const budgetCategoryKey = categoryBudgetMap[requisition.categoryId] || 'otros';
        const allocation = budget.allocations.find((item) => item.category === budgetCategoryKey);
        if (!allocation) {
          errors.push('La categoría seleccionada no existe en el presupuesto del proyecto.');
        } else {
          const alreadyCommitted = purchaseOrders
            .filter((order) => order.parkId === requisition.parkId && isOrderCommitted(order))
            .filter((order) => {
              const orderKey = order.categoryId ? categoryBudgetMap[order.categoryId] : 'otros';
              return orderKey === budgetCategoryKey;
            })
            .reduce((sum, order) => sum + Number(order.totalValue || 0), 0);

          const available = Math.max(Number(allocation.amount || 0) - alreadyCommitted, 0);
          const projectedCommitted = alreadyCommitted + safeEstimate;

          if (projectedCommitted > Number(allocation.amount || 0)) {
            budgetOverrun = {
              allocationId: allocation.id,
              allocationName: allocation.name,
              allocationAvailable: available,
              projectedCommitted,
              overrunAmount: projectedCommitted - Number(allocation.amount || 0)
            };
            if (!allowBudgetOverrun) {
              errors.push(
                `La PR excede presupuesto de la partida ${allocation.name}. Disponible: ${available.toLocaleString('es-CL')}.`
              );
            }
          }
        }
      }

      return { errors, budgetOverrun };
	    },
	    [parks, projectBudgets, purchaseOrders, categoryBudgetMap]
	  );

  const resetApprovalsForModification = (approvals, purchasingManagerId) => ({
    projectManager: {
      userId: approvals?.projectManager?.userId || null,
      status: approvals?.projectManager?.status === 'aprobada' ? 'aprobada' : 'pendiente',
      date: approvals?.projectManager?.date || null,
      note: approvals?.projectManager?.note || ''
    },
    areaManager: {
      userId: approvals?.areaManager?.userId || null,
      status: approvals?.areaManager?.status === 'aprobada' ? 'aprobada' : 'pendiente',
      date: approvals?.areaManager?.date || null,
      note: approvals?.areaManager?.note || ''
    },
    purchasingManager: {
      userId: purchasingManagerId || approvals?.purchasingManager?.userId || null,
      status: 'pendiente',
      date: null,
      note: ''
    }
  });

  const createPurchaseOrder = (orderData) => {
    const validation = validateOrderBusinessRules(orderData, { allowBudgetOverrun: true });
    const businessErrors = validation.errors;
    if (businessErrors.length > 0) {
      throw new Error(businessErrors.join(' '));
    }

    const orderId = generateId('OC');
    const creationDate = format(new Date(), 'yyyy-MM-dd');
    const park = orderData.parkId ? findParkById(orderData.parkId) : null;
    const area = orderData.areaId ? findAreaById(orderData.areaId) : null;
    const derivedCostCenterId = orderData.costCenterId || area?.costCenterId || null;
    const projectManagerId =
      orderData.projectManagerId || park?.managerId || orderData.budgetOwnerId || null;
    const areaManagerId =
      orderData.areaManagerId || area?.areaManagerId || users.find((user) => user.role === 'area_manager')?.id || null;
    const purchasingManagerId =
      orderData.purchasingManagerId || users.find((user) => user.role === 'purchasing_manager')?.id || null;

    const category = orderData.categoryId
      ? categories.find((candidate) => candidate.id === orderData.categoryId)
      : null;

    const approvals = {
      projectManager: {
        userId: projectManagerId,
        status: 'pendiente',
        date: null,
        note: ''
      },
      areaManager: {
        userId: areaManagerId,
        status: 'aprobada',
        date: format(new Date(), 'yyyy-MM-dd'),
        note: 'Aprobación de área omitida (flujo simplificado).'
      },
      purchasingManager: {
        userId: purchasingManagerId,
        status: 'pendiente',
        date: null,
        note: ''
      }
    };

    const items = orderData.items || [];
    const computedTotalValue = orderData.totalValue != null
      ? orderData.totalValue
      : items.reduce((sum, item) => sum + Number(item.unitPrice || 0) * Number(item.quantity || 0), 0);

    const orderAttachments = Array.isArray(orderData.attachments)
      ? orderData.attachments.map((attachment) => ({
          ...attachment,
          id: attachment.id || generateId('ATT')
        }))
      : [];

    const lifecycle = ensurePurchaseOrderLifecycle(orderData || {}).lifecycle;

    const createdOrder = {
      id: orderId,
      linkedRequisitionId: orderData.linkedRequisitionId || null,
      linkedTenderId: orderData.linkedTenderId || null,
      supplierId: orderData.supplierId,
      supplierName: orderData.supplierName,
      type: orderData.type,
      status: 'pendiente_pm',
      createdDate: creationDate,
      deliveryDate: orderData.deliveryDate,
      deliveryLocationId: orderData.deliveryLocationId,
      createdBy: currentUser?.id,
      currency: orderData.currency || 'CLP',
      totalValue: computedTotalValue,
      notes: orderData.notes || '',
      items,
      attachments: orderAttachments,
      parkId: orderData.parkId || park?.id || null,
      areaId: orderData.areaId || area?.id || null,
      costCenterId: derivedCostCenterId,
      categoryId: orderData.categoryId || category?.id || null,
      categoryName: category?.name || orderData.categoryName || null,
      packageId: orderData.packageId || null,
      budgetOwnerId: projectManagerId,
      invoicedValue: orderData.invoicedValue || 0,
      approvals,
      budgetControl: {
        hasOverrun: Boolean(validation.budgetOverrun),
        overrunDetail: validation.budgetOverrun,
        projectManagerAcceptedIncrement: false,
        acceptedByUserId: null,
        acceptedAt: null,
        note: ''
      },
      history: [
        {
          id: generateId('HIST'),
          action: 'Creación de orden',
          status: 'pendiente_pm',
          userId: currentUser?.id,
          timestamp: now(),
          note: orderData.notes || ''
        }
      ],
      lifecycle
    };

    setPurchaseOrders((prev) => [ensurePurchaseOrderLifecycle(createdOrder), ...prev]);
    addAuditRecord('Creación de OC', createdOrder.id, 'Pendiente PM');
    pushNotification({
      title: t('Orden creada'),
      message: t('La orden {0} espera aprobación del Project Manager.', createdOrder.id),
      severity: 'medium'
    });
    return createdOrder;
  };

  const updatePurchaseOrder = (orderId, updates = {}, note = '') => {
    if (!currentUser || !canEditOrder(currentUser.role)) {
      throw new Error(t('No cuentas con permisos para editar órdenes de compra.'));
    }

    const purchasingManager = users.find((user) => user.role === 'purchasing_manager');
    let updatedOrderSnapshot = null;

    const targetOrder = purchaseOrders.find((order) => order.id === orderId);
    if (!targetOrder) {
      throw new Error(t('No fue posible localizar la orden {0} para actualizarla.', orderId));
    }
    const validation = validateOrderBusinessRules(
      {
        ...targetOrder,
        ...updates
      },
      { allowBudgetOverrun: true }
    );
    if (validation.errors.length > 0) {
      throw new Error(validation.errors.join(' '));
    }

    setPurchaseOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;

        const merged = {
          ...order,
          ...updates,
          budgetControl: {
            hasOverrun: Boolean(validation.budgetOverrun),
            overrunDetail: validation.budgetOverrun,
            projectManagerAcceptedIncrement:
              Boolean(validation.budgetOverrun) ? false : order.budgetControl?.projectManagerAcceptedIncrement || false,
            acceptedByUserId: Boolean(validation.budgetOverrun) ? null : order.budgetControl?.acceptedByUserId || null,
            acceptedAt: Boolean(validation.budgetOverrun) ? null : order.budgetControl?.acceptedAt || null,
            note: Boolean(validation.budgetOverrun) ? '' : order.budgetControl?.note || ''
          },
          approvals: resetApprovalsForModification(order.approvals, purchasingManager?.id),
          status: 'pendiente_compras',
          history: [
            {
              id: generateId('HIST'),
              action: 'Orden modificada',
              status: 'pendiente_compras',
              userId: currentUser.id,
              timestamp: now(),
              note: note || 'Actualización realizada por finanzas/compras.'
            },
            ...(order.history || [])
          ]
        };

        updatedOrderSnapshot = merged;
        return merged;
      })
    );

    if (!updatedOrderSnapshot) {
      throw new Error(t('No fue posible localizar la orden {0} para actualizarla.', orderId));
    }

    pushNotification({
      title: t('OC {0} requiere aprobación', orderId),
      message: t('Gerencia de Compras debe revisar los ajustes realizados.'),
      severity: 'medium'
    });

    addAuditRecord('Modificación OC', orderId, 'Pendiente Compras');
    return updatedOrderSnapshot;
  };

  const createPurchaseRequisition = (requisitionData, { submit = false } = {}) => {
    const requisitionId = computeNextPurchaseRequisitionId(purchaseRequisitions);
    const creationDate = format(new Date(), 'yyyy-MM-dd');
    const area = requisitionData.areaId ? findAreaById(requisitionData.areaId) : null;
    const park = requisitionData.parkId ? findParkById(requisitionData.parkId) : area ? { id: area.parkId, name: area.parkName } : null;
    const developmentProject =
      park?.id ? (renewableProjects || []).find((project) => project.parkId === park.id) : null;
    const developmentApproved =
      !developmentProject ||
      (Boolean(developmentProject?.approval?.projectManager) &&
        Boolean(developmentProject?.approval?.purchasingManager));
    if (submit && !developmentApproved) {
      throw new Error(
        t(
          'Proyecto aún no está aprobado en Desarrollo (aprobación dual: Gerente de Proyecto + Gerente de Compras). No se puede emitir PR.'
        )
      );
    }
    const derivedCostCenterId = requisitionData.costCenterId || area?.costCenterId || null;
    const parkTeam = park?.id ? findParkById(park.id)?.team || null : null;
    const areaManagerId =
      area?.areaManagerId || parkTeam?.areaManagers?.[0] || users.find((user) => user.role === 'area_manager')?.id || null;
    const purchaseControllerId =
      parkTeam?.controllerId || users.find((user) => user.role === 'purchase_controller')?.id || null;
    const status = submit ? 'pendiente_area' : 'borrador';

    const estimatedTotal = Array.isArray(requisitionData.lines)
      ? requisitionData.lines.reduce(
          (sum, line) => sum + Number(line.quantity || 0) * Number(line.unitPrice || 0),
          0
        )
      : 0;

    const newRequisition = {
      id: requisitionId,
      title: requisitionData.title || t('Solicitud de compra'),
      createdDate: creationDate,
      neededDate: requisitionData.neededDate || null,
      requesterId: requisitionData.requesterId || currentUser?.id,
      requesterName: requisitionData.requesterName || currentUser?.name,
      parkId: requisitionData.parkId || park?.id || null,
      areaId: requisitionData.areaId || area?.id || null,
      costCenterId: derivedCostCenterId,
      categoryId: requisitionData.categoryId || null,
      packageId: requisitionData.packageId || null,
      type: requisitionData.type || 'material',
      status,
      priority: requisitionData.priority || 'media',
      justification: requisitionData.justification || '',
      estimatedTotal,
      lines: requisitionData.lines || [],
      attachments: Array.isArray(requisitionData.attachments)
        ? requisitionData.attachments.map((attachment) => ({
            ...attachment,
            id: attachment.id || generateId('ATT')
          }))
        : [],
      routingCode: requisitionData.routingCode || '',
      approvals: {
        areaManager: {
          userId: areaManagerId,
          status: submit ? 'pendiente' : 'pendiente',
          date: null,
          note: ''
        },
        purchaseController: {
          userId: purchaseControllerId,
          status: 'pendiente',
          date: null,
          note: ''
        }
      },
      routedToBuyerId: null,
      linkedOrderId: null,
      budgetControl: null,
      history: [
        {
          id: generateId('PRH'),
          action: submit ? 'Requisición enviada' : 'Borrador creado',
          status,
          userId: currentUser?.id,
          timestamp: now(),
          note: requisitionData.justification || ''
        }
      ]
    };

    setPurchaseRequisitions((prev) => [newRequisition, ...prev]);
    addAuditRecord('Creación requisición', requisitionId, status);

    if (submit && areaManagerId) {
      const approver = findUserById(areaManagerId);
      pushNotification({
        title: t('Nueva requisición {0}', requisitionId),
        message: approver ? t('Pendiente aprobación de {0}.', approver.name) : t('Pendiente aprobación de área.'),
        severity: 'medium'
      });
    }

    return newRequisition;
  };

	  const submitPurchaseRequisition = (requisitionId) => {
	    const current = purchaseRequisitions.find((candidate) => candidate.id === requisitionId) || null;
	    const parkId = current?.parkId || null;
	    const developmentProject =
	      parkId ? (renewableProjects || []).find((project) => project.parkId === parkId) : null;
	    const developmentApproved =
	      !developmentProject ||
	      (Boolean(developmentProject?.approval?.projectManager) &&
	        Boolean(developmentProject?.approval?.purchasingManager));

	    if (current && current.status === 'borrador' && !developmentApproved) {
	      const message =
	        'El proyecto aún no tiene aprobación dual en Desarrollo (Gerente de Proyecto + Gerente de Compras). Completa la aprobación en Desarrollo > Portafolio Renovables y reintenta.';
	      pushNotification({
	        title: t('PR {0} bloqueada', requisitionId),
	        message,
	        severity: 'high'
	      });
	      addAuditRecord('Bloqueo envío PR', requisitionId, 'Desarrollo no aprobado');
	      setPurchaseRequisitions((prev) =>
	        prev.map((req) => {
          if (req.id !== requisitionId) return req;
          if (req.status !== 'borrador') return req;
          return {
	            ...req,
	            history: [
	              {
	                id: generateId('PRH'),
	                action: 'Envío bloqueado',
	                status: req.status,
	                userId: currentUser?.id,
	                timestamp: now(),
	                note:
	                  'Proyecto no aprobado en Desarrollo (aprobación dual requerida).'
	              },
	              ...(req.history || [])
	            ]
	          };
	        })
	      );
	      throw new Error(message);
	    }

	    setPurchaseRequisitions((prev) =>
	      prev.map((req) => {
	        if (req.id !== requisitionId) return req;
	        if (req.status !== 'borrador') return req;

	        const validation = validateRequisitionBusinessRules(req, { allowBudgetOverrun: true });
	        if (validation.errors.length > 0) {
	          const message = validation.errors.join(' ');
	          pushNotification({
	            title: t('PR {0} bloqueada', req.id),
	            message,
	            severity: 'high'
	          });
	          addAuditRecord('Bloqueo envío PR', req.id, 'Regla de negocio');
	          return {
	            ...req,
	            history: [
	              {
	                id: generateId('PRH'),
	                action: 'Envío bloqueado',
	                status: req.status,
	                userId: currentUser?.id,
	                timestamp: now(),
	                note: message
	              },
	              ...(req.history || [])
	            ]
	          };
	        }
	        const budgetControl = {
	          hasOverrun: Boolean(validation.budgetOverrun),
	          overrunDetail: validation.budgetOverrun,
	          controllerAcceptedOverrun: false,
          acceptedByUserId: null,
          acceptedAt: null,
          note: ''
        };

        const updated = {
          ...req,
          status: 'pendiente_area',
          budgetControl,
          history: [
            {
              id: generateId('PRH'),
              action: 'Requisición enviada',
              status: 'pendiente_area',
              userId: currentUser?.id,
              timestamp: now(),
              note: ''
            },
            ...req.history
          ]
        };
        const approver = updated.approvals?.areaManager?.userId
          ? findUserById(updated.approvals.areaManager.userId)
          : null;
        if (approver) {
          pushNotification({
            title: t('Requisición {0} pendiente', req.id),
            message: t('Solicita aprobación de {0}.', approver.name),
            severity: 'medium'
          });
        }
	        addAuditRecord('Envío requisición', req.id, 'Pendiente área');
	        return updated;
	      })
	    );
	    if (current && current.status === 'borrador') {
	      const validation = validateRequisitionBusinessRules(current, { allowBudgetOverrun: true });
	      if (validation.errors.length > 0) {
	        throw new Error(validation.errors.join(' '));
	      }
	    }
	  };

  const setRequisitionBudgetOverrunAcceptance = (requisitionId, accepted, note = '') => {
    if (!currentUser) return;
    setPurchaseRequisitions((prev) =>
      prev.map((req) => {
        if (req.id !== requisitionId) return req;
        if (!req.budgetControl?.hasOverrun) return req;
        if (req.status !== 'pendiente_controller') return req;

        const controllerId = req.approvals?.purchaseController?.userId || null;
        const canAct =
          currentUser.role === 'super_user' || (controllerId && currentUser.id === controllerId);
        if (!canAct) return req;

        return {
          ...req,
          budgetControl: {
            ...(req.budgetControl || {}),
            controllerAcceptedOverrun: Boolean(accepted),
            acceptedByUserId: accepted ? currentUser.id : null,
            acceptedAt: accepted ? now() : null,
            note: accepted ? note || '' : ''
          },
          history: [
            {
              id: generateId('PRH'),
              action: accepted ? 'Aceptación sobrepresupuesto controller' : 'Revocación sobrepresupuesto controller',
              status: req.status,
              userId: currentUser.id,
              timestamp: now(),
              note: note || ''
            },
            ...(req.history || [])
          ]
        };
      })
    );
  };

  const updateRequisitionApproval = (requisitionId, stage, decision = true, note = '') => {
    const stageMap = {
      area: 'areaManager',
      controller: 'purchaseController'
    };
    const stageLabels = {
      area: 'Área',
      controller: 'Control Compras'
    };

    const stageKey = stageMap[stage];
    if (!stageKey) return;

    let updatedRequisitionSnapshot = null;

    setPurchaseRequisitions((prev) =>
      prev.map((req) => {
        if (req.id !== requisitionId) return req;
        if (!req.approvals || !req.approvals[stageKey]) return req;

        const assignedApproverId = req.approvals[stageKey]?.userId || null;
        const canAct =
          currentUser?.role === 'super_user' ||
          (assignedApproverId && currentUser?.id && currentUser.id === assignedApproverId);
        if (!canAct) return req;

        const expectedStatus = stage === 'area' ? 'pendiente_area' : 'pendiente_controller';
        if (req.status !== expectedStatus) return req;

        if (decision && stage === 'controller' && req.budgetControl?.hasOverrun) {
          if (!req.budgetControl?.controllerAcceptedOverrun) {
            return req;
          }
        }

        const decisionStatus = decision ? 'aprobada' : 'rechazada';
        const decisionDate = format(new Date(), 'yyyy-MM-dd');
        const approvals = {
          ...req.approvals,
          [stageKey]: {
            ...req.approvals[stageKey],
            status: decisionStatus,
            date: decisionDate,
            note,
            userId: currentUser?.id || req.approvals[stageKey].userId
          }
        };

        const nextStatus = decision
          ? stage === 'area'
            ? 'pendiente_controller'
            : 'pendiente_compra'
          : 'rechazada';
        const historyAction = decision
          ? `Aprobación ${stageLabels[stage]}`
          : `Rechazo ${stageLabels[stage]}`;

        const updatedRequisition = {
          ...req,
          approvals,
          status: nextStatus,
          routedToBuyerId: decision ? req.routedToBuyerId : null,
          history: [
            {
              id: generateId('PRH'),
              action: historyAction,
              status: nextStatus,
              userId: currentUser?.id,
              timestamp: now(),
              note
            },
            ...(req.history || [])
          ]
        };

        updatedRequisitionSnapshot = {
          before: req,
          after: updatedRequisition,
          decision,
          stage
        };

        return updatedRequisition;
      })
    );

    if (!updatedRequisitionSnapshot) {
      return;
    }

    const { after, before } = updatedRequisitionSnapshot;

    if (decision) {
      if (stage === 'area') {
        const controllerId = after.approvals?.purchaseController?.userId;
        const controller = controllerId ? findUserById(controllerId) : null;
        pushNotification({
          title: t('Requisición {0} aprobada por Área', after.id),
          message: controller
            ? t('Pendiente revisión de {0}.', controller.name)
            : t('Pendiente revisión de Control Compras.'),
          severity: 'medium'
        });
      } else if (stage === 'controller') {
        const buyers = users.filter((user) => user.role === 'comprador');
        const buyerNames =
          buyers.length > 0 ? buyers.map((buyer) => buyer.name).join(', ') : 'el equipo de Compras';
        pushNotification({
          title: t('Requisición {0} lista para compras', after.id),
          message: t('Disponible para asignar a {0}.', buyerNames),
          severity: 'info'
        });
      }
    } else {
      const requester = before.requesterId ? findUserById(before.requesterId) : null;
      pushNotification({
        title: t('Requisición {0} rechazada', before.id),
        message: requester
          ? `Se notificó a ${requester.name}. ${note || ''}`.trim()
          : note || t('La requisición fue rechazada en el flujo de aprobación.'),
        severity: 'medium'
      });
    }

    addAuditRecord(
      decision ? `Aprobación requisición ${stageLabels[stage]}` : `Rechazo requisición ${stageLabels[stage]}`,
      requisitionId,
      decision ? 'Aprobada etapa' : 'Rechazada'
    );
  };

  const approveRequisitionFlow = (requisitionId, currentRole) => {
    if (currentRole === 'area_manager') {
      updateRequisitionApproval(requisitionId, 'area', true);
    } else if (currentRole === 'purchase_controller') {
      updateRequisitionApproval(requisitionId, 'controller', true);
    }
  };

  const createRequisition = (requisition) => {
    setPurchaseRequisitions((prev) => [
      {
        id: generateId('REQ'),
        ...requisition,
        status: 'Pending Approval',
        createdBy: currentUser.id,
        createdDate: now(),
        history: [
          {
            id: generateId('HIST'),
            action: 'Created',
            status: 'Pending Approval',
            userId: currentUser.id,
            timestamp: now(),
            note: 'Requisition created.'
          }
        ]
      },
      ...prev
    ]);
  };

  const approveRequisition = (requisitionId, approverId) => {
    setPurchaseRequisitions((prev) =>
      prev.map((req) =>
        req.id === requisitionId
          ? {
              ...req,
              status: 'Approved',
              history: [
                ...req.history,
                {
                  id: generateId('HIST'),
                  action: 'Approved',
                  status: 'Approved',
                  userId: approverId,
                  timestamp: now(),
                  note: 'Requisition approved.'
                }
              ]
            }
          : req
      )
    );
  };

  const assignRequisitionToBuyer = (requisitionId, buyerId, routingCode = '') => {
    if (!currentUser || !['purchase_controller', 'super_user'].includes(currentUser.role)) {
      throw new Error(t(
        'Solo Purchase Controller (o super_user) puede derivar requisiciones a compradores.'
      ));
    }
    setPurchaseRequisitions((prev) =>
      prev.map((req) =>
        req.id === requisitionId
          ? {
              ...req,
              assignedTo: buyerId,
              routedToBuyerId: buyerId,
              routingCode: routingCode || req.routingCode || '',
              status: 'pendiente_compra',
              history: [
                ...req.history,
                {
                  id: generateId('HIST'),
                  action: 'Derivada a Comprador',
                  status: 'pendiente_compra',
                  userId: currentUser.id,
                  timestamp: now(),
                  note: `Asignada a comprador ${buyerId}${routingCode ? ` · Código ${routingCode}` : ''}.`
                }
              ]
            }
          : req
      )
    );
    const buyer = findUserById(buyerId);
    pushNotification({
      title: t('PR {0} asignada', requisitionId),
      message:
        buyer && routingCode
          ? t('{0} atenderá la solicitud. Código: {1}.', buyer.name, routingCode)
          : buyer
          ? t('{0} atenderá la solicitud.', buyer.name)
          : routingCode
          ? t('Código interno asignado: {0}.', routingCode)
          : t('Solicitud derivada a comprador.'),
      severity: 'info'
    });
  };

  const convertRequisitionToOrder = (requisitionId, orderData = {}) => {
    const requisition = purchaseRequisitions.find((req) => req.id === requisitionId);
    if (!requisition || !['pendiente_compra', 'en_licitacion'].includes(requisition.status)) {
      return null;
    }

    const canConvert =
      currentUser?.role === 'super_user' ||
      (currentUser?.role === 'comprador' &&
        requisition.routedToBuyerId &&
        requisition.routedToBuyerId === currentUser.id);
    if (!canConvert) {
      throw new Error(t(
        'Solo el comprador asignado (o super_user) puede generar una OC desde la requisición.'
      ));
    }

    if (!orderData.supplierId) {
      throw new Error(t('Debes asignar proveedor para generar la OC.'));
    }
    if (!orderData.categoryId) {
      throw new Error(t('Debes asignar categoría presupuestaria para generar la OC.'));
    }

    const supplier = suppliers.find((sup) => sup.id === orderData.supplierId);
    const newOrder = createPurchaseOrder({
      linkedRequisitionId: requisition.id,
      linkedTenderId: orderData.tenderId || requisition.tenderId || null,
      supplierId: orderData.supplierId,
      supplierName: supplier?.name || orderData.supplierName || 'Proveedor por definir',
      type: requisition.type === 'servicio' ? 'nacional' : 'internacional',
      deliveryLocationId: orderData.deliveryLocationId || requisition.deliveryLocationId || locations[0]?.id,
      deliveryDate: orderData.deliveryDate || requisition.neededDate,
      notes: `${requisition.title} · Generado desde ${requisition.id}${
        requisition.routingCode ? ` · Código ${requisition.routingCode}` : ''
      }`,
      currency: orderData.currency || 'CLP',
      parkId: requisition.parkId,
      areaId: requisition.areaId,
      costCenterId: requisition.costCenterId,
      categoryId: orderData.categoryId || null,
      packageId: requisition.packageId || null,
      items: (orderData.items && orderData.items.length > 0 ? orderData.items : requisition.lines).map((line) => ({
        sku: line.sku || null,
        description: line.description,
        quantity: line.quantity,
        unit: line.unit || 'unidad',
        unitPrice: line.unitPrice || 0
      })),
      attachments: orderData.attachments && orderData.attachments.length > 0
        ? orderData.attachments
        : requisition.attachments || [],
      totalValue: orderData.totalValue || 0
    });

    setPurchaseRequisitions((prev) =>
      prev.map((req) => {
        if (req.id !== requisitionId) return req;
        return {
          ...req,
          status: 'convertida',
          linkedOrderId: newOrder?.id || null,
          history: [
            {
              id: generateId('PRH'),
              action: 'Convertida a OC',
              status: 'convertida',
              userId: currentUser?.id,
              timestamp: now(),
              note: newOrder ? `Generada ${newOrder.id}` : ''
            },
            ...req.history
          ]
        };
      })
    );

    addAuditRecord('Conversión requisición', requisitionId, newOrder?.id || 'Sin OC');
    return newOrder;
  };

  const canManageProjectFinance = (role) =>
    ['project_manager', 'super_user'].includes(role);

  const upsertProjectBudgetAllocation = useCallback(
    (parkId, allocationPayload) => {
      if (!parkId || !allocationPayload || !currentUser) {
        throw new Error(t('Datos incompletos para actualizar presupuesto.'));
      }
      if (!canManageProjectFinance(currentUser.role)) {
        throw new Error(t('No cuentas con permisos para editar presupuestos.'));
      }

      const amount = Number(allocationPayload.amount || 0);
      const safeAmount = Number.isFinite(amount) ? Math.max(amount, 0) : 0;
      // Se captura acá afuera para poder sincronizar con backend/core después,
      // con el mismo id que terminó usando el estado local (nuevo o existente).
      let syncedAllocation = null;

      setProjectBudgets((prev) => {
        const existing = prev.find((budget) => budget.parkId === parkId);
        if (!existing) {
          const newAllocation = {
            id: allocationPayload.id || generateId('BGT'),
            name: allocationPayload.name || 'Nueva partida',
            category: allocationPayload.category || 'otros',
            amount: safeAmount
          };
          syncedAllocation = newAllocation;
          return [
            ...prev,
            {
              parkId,
              currency: allocationPayload.currency || 'USD',
              total: safeAmount,
              allocations: [newAllocation]
            }
          ];
        }

        const allocations = [...(existing.allocations || [])];
        const index = allocations.findIndex((item) => item.id === allocationPayload.id);
        if (index >= 0) {
          allocations[index] = {
            ...allocations[index],
            name: allocationPayload.name || allocations[index].name,
            category: allocationPayload.category || allocations[index].category,
            amount: safeAmount
          };
          syncedAllocation = allocations[index];
        } else {
          const newAllocation = {
            id: allocationPayload.id || generateId('BGT'),
            name: allocationPayload.name || 'Nueva partida',
            category: allocationPayload.category || 'otros',
            amount: safeAmount
          };
          allocations.push(newAllocation);
          syncedAllocation = newAllocation;
        }

        const nextTotal = allocations.reduce((sum, item) => sum + Number(item.amount || 0), 0);
        return prev.map((budget) =>
          budget.parkId === parkId
            ? {
                ...budget,
                currency: allocationPayload.currency || budget.currency || 'USD',
                allocations,
                total: nextTotal
              }
            : budget
        );
      });

      addAuditRecord('Edición presupuesto proyecto', parkId, allocationPayload.name || 'Partida');
      if (syncedAllocation) {
        projectsApi.upsertAllocation(parkId, syncedAllocation).catch((error) => {
          console.warn('No fue posible sincronizar la partida presupuestaria con backend/core.', error);
        });
      }
    },
    [currentUser, addAuditRecord]
  );

  const removeProjectBudgetAllocation = useCallback(
    (parkId, allocationId) => {
      if (!parkId || !allocationId || !currentUser) return;
      if (!canManageProjectFinance(currentUser.role)) return;
      setProjectBudgets((prev) =>
        prev.map((budget) => {
          if (budget.parkId !== parkId) return budget;
          const nextAllocations = (budget.allocations || []).filter((item) => item.id !== allocationId);
          const nextTotal = nextAllocations.reduce((sum, item) => sum + Number(item.amount || 0), 0);
          return {
            ...budget,
            allocations: nextAllocations,
            total: nextTotal
          };
        })
      );
      addAuditRecord('Eliminación partida presupuesto', `${parkId}-${allocationId}`, 'Eliminada');
      projectsApi.removeAllocation(parkId, allocationId).catch((error) => {
        console.warn('No fue posible eliminar la partida presupuestaria en backend/core.', error);
      });
    },
    [currentUser, addAuditRecord]
  );

  const upsertProjectIncome = useCallback(
    (incomePayload) => {
      if (!incomePayload?.parkId || !currentUser) {
        throw new Error(t('Datos incompletos para registrar ingreso por hito.'));
      }
      if (!canManageProjectFinance(currentUser.role)) {
        throw new Error(t('No cuentas con permisos para gestionar ingresos.'));
      }

      const amount = Math.max(Number(incomePayload.amount || 0), 0);
      const receivedAmount = Math.max(Number(incomePayload.receivedAmount || 0), 0);

      setProjectIncomes((prev) => {
        const exists = prev.some((item) => item.id === incomePayload.id);
        if (!exists) {
          return [
            {
              id: incomePayload.id || generateId('INC'),
              parkId: incomePayload.parkId,
              title: incomePayload.title || t('Ingreso por hito'),
              milestoneId: incomePayload.milestoneId || null,
              expectedDate: incomePayload.expectedDate || null,
              amount,
              status: incomePayload.status || 'pendiente',
              receivedAmount
            },
            ...prev
          ];
        }
        return prev.map((item) =>
          item.id === incomePayload.id
            ? {
                ...item,
                ...incomePayload,
                amount,
                receivedAmount
              }
            : item
        );
      });

      addAuditRecord(
        'Ingreso por hito',
        incomePayload.id || incomePayload.parkId,
        incomePayload.status || 'Actualizado'
      );
    },
    [currentUser, addAuditRecord]
  );

  const getProjectBudgetSummary = (parkId) => {
    const budget = projectBudgets.find((item) => item.parkId === parkId);
    if (!budget) {
      return {
        parkId,
        parkName: findParkById(parkId)?.name || '-',
        currency: 'USD',
        totalBudget: 0,
        totalCommitted: 0,
        totalInvoiced: 0,
        totalIncomePlanned: 0,
        totalIncomeReceived: 0,
        available: 0,
        marginForecast: 0,
        marginReal: 0,
        incomes: [],
        allocations: []
      };
    }

    const relevantOrders = purchaseOrders.filter((order) => order.parkId === parkId && order.status !== 'rechazada');
    const committedOrders = relevantOrders.filter((order) => isOrderCommitted(order));

    const allocations = budget.allocations.map((allocation) => {
      const committed = committedOrders
        .filter((order) => {
          const key = order.categoryId ? categoryBudgetMap[order.categoryId] : 'otros';
          return key === allocation.category;
        })
        .reduce((sum, order) => sum + (order.totalValue || 0), 0);

      const invoiced = relevantOrders
        .filter((order) => {
          const key = order.categoryId ? categoryBudgetMap[order.categoryId] : 'otros';
          return key === allocation.category;
        })
        .reduce((sum, order) => sum + (order.invoicedValue || 0), 0);

      return {
        ...allocation,
        committed,
        invoiced,
        available: allocation.amount - committed
      };
    });

    const totalCommitted = allocations.reduce((sum, item) => sum + item.committed, 0);
    const totalInvoiced = allocations.reduce((sum, item) => sum + item.invoiced, 0);
    const incomes = projectIncomes.filter((income) => income.parkId === parkId);
    const totalIncomePlanned = incomes.reduce((sum, income) => sum + Number(income.amount || 0), 0);
    const totalIncomeReceived = incomes.reduce((sum, income) => sum + Number(income.receivedAmount || 0), 0);

    return {
      parkId,
      parkName: findParkById(parkId)?.name || '-',
      currency: budget.currency,
      totalBudget: budget.total,
      totalCommitted,
      totalInvoiced,
      totalIncomePlanned,
      totalIncomeReceived,
      available: budget.total - totalCommitted,
      marginForecast: totalIncomePlanned - totalCommitted,
      marginReal: totalIncomeReceived - totalInvoiced,
      incomes,
      allocations
    };
  };

  const projectFinancialOverview = useMemo(() => {
    return parks.map((park) => getProjectBudgetSummary(park.id));
  }, [parks, projectBudgets, purchaseOrders, categoryBudgetMap, projectIncomes]);

  const advancePurchaseOrderApproval = (orderId, stage, decision = true, note = '') => {
    const stageMap = {
      project: 'projectManager',
      purchasing: 'purchasingManager'
    };

    const stageKey = stageMap[stage];
    if (!stageKey) return;

    if (decision && stage === 'purchasing') {
      const currentOrder = purchaseOrders.find((order) => order.id === orderId);
      if (currentOrder) {
        const validation = validateOrderBusinessRules(currentOrder, { allowBudgetOverrun: true });
        const errors = validation.errors;
        const requiresPmIncrementAcceptance = Boolean(validation.budgetOverrun);
        const pmAccepted = Boolean(currentOrder.budgetControl?.projectManagerAcceptedIncrement);
        if (errors.length > 0 || (requiresPmIncrementAcceptance && !pmAccepted)) {
          const messages = [...errors];
          if (requiresPmIncrementAcceptance && !pmAccepted) {
            messages.push(
              'La OC excede presupuesto y requiere aceptación explícita del incremento por el Gerente de Proyecto.'
            );
          }
          pushNotification({
            title: t('OC {0} bloqueada', orderId),
            message: messages.join(' '),
            severity: 'high'
          });
          addAuditRecord('Bloqueo aprobación OC', orderId, 'Regla de negocio');
          return;
        }
      }
    }

    setPurchaseOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;

        const approvalNode = order.approvals?.[stageKey];
        if (!approvalNode || approvalNode.status !== 'pendiente') {
          return order;
        }

        const assignedApproverId = approvalNode.userId || null;
        const canAct =
          currentUser?.role === 'super_user' ||
          (assignedApproverId && currentUser?.id && currentUser.id === assignedApproverId);
        if (!canAct) {
          return order;
        }

        const decisionStatus = decision ? 'aprobada' : 'rechazada';
        const decisionDate = format(new Date(), 'yyyy-MM-dd');
        const approvals = {
          ...order.approvals,
          [stageKey]: {
            ...approvalNode,
            status: decisionStatus,
            date: decisionDate,
            note,
            userId: currentUser?.id || approvalNode.userId
          }
        };

        let nextStatus = order.status;
        if (!decision) {
          nextStatus = 'rechazada';
        } else if (stage === 'project') {
          nextStatus = 'pendiente_compras';
        } else if (stage === 'purchasing') {
          nextStatus = 'aprobada';
        }

        const updatedOrder = {
          ...order,
          approvals,
          status: nextStatus,
          approvedDate: stage === 'purchasing' && decision ? decisionDate : order.approvedDate,
          history: [
            {
              id: generateId('HIST'),
              action: decision ? `Aprobación ${stage}` : `Rechazo ${stage}`,
              status: nextStatus,
              userId: currentUser?.id,
              timestamp: now(),
              note
            },
            ...(order.history || [])
          ]
        };

        if (decision) {
          if (nextStatus === 'pendiente_area') {
            const approver = approvals.areaManager?.userId ? findUserById(approvals.areaManager.userId) : null;
            pushNotification({
              title: t('OC {0} espera aprobación de Área', order.id),
              message: approver ? t('Se solicitó aprobación a {0}.', approver.name) : t('Se solicitó aprobación de Área.'),
              severity: 'medium'
            });
          } else if (nextStatus === 'pendiente_compras') {
            const approver = approvals.purchasingManager?.userId ? findUserById(approvals.purchasingManager.userId) : null;
            pushNotification({
              title: t('OC {0} espera aprobación Compras', order.id),
              message: approver ? t('Se solicitó aprobación a {0}.', approver.name) : t('Pendiente aprobación Gerencia de Compras.'),
              severity: 'medium'
            });
          } else if (nextStatus === 'aprobada') {
            pushNotification({
              title: t('OC {0} aprobada', order.id),
              message: t('La orden queda disponible para recepción.'),
              severity: 'info'
            });
          }
        } else {
          pushNotification({
            title: t('OC {0} rechazada', order.id),
            message: note || t('La orden fue rechazada en el flujo de aprobación.'),
            severity: 'medium'
          });
        }

        return updatedOrder;
      })
    );

    addAuditRecord(
      decision ? 'Aprobación OC' : 'Rechazo OC',
      `${orderId} · ${stage}`,
      decision ? 'Aprobada etapa' : 'Rechazada'
    );
  };

  const setPurchaseOrderBudgetIncrementAcceptance = useCallback(
    (orderId, accepted, note = '') => {
      if (!orderId || !currentUser) return;
      setPurchaseOrders((prev) =>
        prev.map((order) => {
          if (order.id !== orderId) return order;
          const pmId = order.approvals?.projectManager?.userId || null;
          const canAct = currentUser.role === 'super_user' || (pmId && pmId === currentUser.id);
          if (!canAct) return order;
          if (!order.budgetControl?.hasOverrun) return order;

          const nextBudgetControl = {
            ...(order.budgetControl || {}),
            projectManagerAcceptedIncrement: Boolean(accepted),
            acceptedByUserId: accepted ? currentUser.id : null,
            acceptedAt: accepted ? now() : null,
            note: accepted ? note || order.budgetControl?.note || '' : ''
          };

          return {
            ...order,
            budgetControl: nextBudgetControl,
            history: [
              {
                id: generateId('HIST'),
                action: accepted ? 'Aceptación incremento budget PM' : 'Revocación incremento budget PM',
                status: order.status,
                userId: currentUser.id,
                timestamp: now(),
                note: note || ''
              },
              ...(order.history || [])
            ]
          };
        })
      );
      addAuditRecord(
        accepted ? 'Aprobación incremento budget PM' : 'Revocación incremento budget PM',
        orderId,
        accepted ? 'Aceptado' : 'Pendiente'
      );
    },
    [currentUser, addAuditRecord]
  );

  const markPurchaseOrderReceived = (orderId, note = '', receivedDate) => {
    if (!currentUser) return;
    setPurchaseOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;
        const ensuredOrder = ensurePurchaseOrderLifecycle(order);
        const canReceive =
          RECEIVING_ALLOWED_ROLES.has(currentUser.role) || currentUser.id === ensuredOrder.createdBy;
        if (!canReceive) return ensuredOrder;
        if (
          ensuredOrder.lifecycle.received.status === 'completed' ||
          (ensuredOrder.status !== 'aprobada' && ensuredOrder.status !== 'recibida')
        ) {
          return ensuredOrder;
        }

        const effectiveDate = receivedDate || format(new Date(), 'yyyy-MM-dd');
        let updatedOrder = {
          ...ensuredOrder,
          status: 'recibida',
          receivedDate: effectiveDate,
          history: [
            {
              id: generateId('HIST'),
              action: 'Recepción de materiales',
              status: 'recibida',
              userId: currentUser?.id,
              timestamp: now(),
              note
            },
            ...(ensuredOrder.history || [])
          ]
        };

        updatedOrder = applyLifecycleUpdate(updatedOrder, 'received', {
          userId: currentUser.id,
          note,
          timestamp: now()
        });

        const inventoryLines = (updatedOrder.items || []).filter((item) => item?.sku);
        if (inventoryLines.length > 0) {
          inventoryLines.forEach((item) => {
            adjustInventory(item.sku, order.deliveryLocationId, item.quantity);
            registerMovement({
              type: 'ingreso',
              sku: item.sku,
              quantity: item.quantity,
              locationId: order.deliveryLocationId,
              reference: order.id,
              parkId: order.parkId,
              areaId: order.areaId,
              costCenterId: order.costCenterId,
              userId: currentUser?.id
            });
          });
          pushNotification({
            title: t('OC {0} recepcionada', order.id),
            message: t('El inventario fue actualizado con el material recibido.'),
            severity: 'info'
          });
        }

        return updatedOrder;
      })
    );

    addAuditRecord('Recepción OC', orderId, 'Recibida');
  };

  const markPurchaseOrderInvoiced = (orderId, { invoiceId = null, note = '' } = {}) => {
    if (!currentUser || !FINANCE_ALLOWED_ROLES.has(currentUser.role)) return;
    setPurchaseOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;
        const ensuredOrder = ensurePurchaseOrderLifecycle(order);
        if (ensuredOrder.lifecycle.invoiced.status === 'completed') return ensuredOrder;
        const updatedOrder = applyLifecycleUpdate(ensuredOrder, 'invoiced', {
          userId: currentUser.id,
          note: note || (invoiceId ? `Factura ${invoiceId} ligada a la OC.` : 'OC facturada.'),
          referenceId: invoiceId,
          timestamp: now()
        });
        return {
          ...updatedOrder,
          history: [
            {
              id: generateId('HIST'),
              action: 'Factura registrada',
              status: updatedOrder.status,
              userId: currentUser.id,
              timestamp: now(),
              note: note || (invoiceId ? `Factura ${invoiceId} posteada.` : 'Factura posteada.')
            },
            ...(updatedOrder.history || [])
          ]
        };
      })
    );
    addAuditRecord('OC facturada', orderId, invoiceId || 'Sin referencia');
  };

  const markPurchaseOrderPaid = (
    orderId,
    { paymentReference = null, note = '' } = {}
  ) => {
    if (!currentUser || !FINANCE_ALLOWED_ROLES.has(currentUser.role)) return;
    setPurchaseOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;
        const ensuredOrder = ensurePurchaseOrderLifecycle(order);
        if (ensuredOrder.lifecycle.paid.status === 'completed') return ensuredOrder;
        const updatedOrder = applyLifecycleUpdate(ensuredOrder, 'paid', {
          userId: currentUser.id,
          note: note || (paymentReference ? `Pago ${paymentReference}` : 'Pago aplicado.'),
          referenceId: paymentReference,
          timestamp: now()
        });
        return {
          ...updatedOrder,
          history: [
            {
              id: generateId('HIST'),
              action: 'Pago confirmado',
              status: updatedOrder.status,
              userId: currentUser.id,
              timestamp: now(),
              note: note || (paymentReference ? `Pago ${paymentReference} conciliado.` : 'Pago conciliado.')
            },
            ...(updatedOrder.history || [])
          ]
        };
      })
    );
    addAuditRecord('OC pagada', orderId, paymentReference || 'Sin referencia');
  };

  const markPurchaseOrderSent = (orderId, note = '') => {
    if (!currentUser) return;
    setPurchaseOrders((prev) =>
      prev.map((order) => {
        if (order.id !== orderId) return order;
        if (order.status !== 'aprobada') return order;
        return {
          ...order,
          status: 'enviada',
          history: [
            {
              id: generateId('HIST'),
              action: 'Enviada a proveedor',
              status: 'enviada',
              userId: currentUser.id,
              timestamp: now(),
              note: note || 'Orden enviada a proveedor.'
            },
            ...(order.history || [])
          ]
        };
      })
    );
    addAuditRecord('OC enviada', orderId, 'Proveedor');
  };

  const createShipment = (payload) => {
    const reference = payload.reference || `DESP-${Math.floor(Math.random() * 900 + 100)}`;
    const destination = locations.find((loc) => loc.id === payload.destinationLocationId);
    const newShipment = {
      id: generateId('SHP'),
      reference,
      status: 'en tránsito',
      createdAt: format(new Date(), 'yyyy-MM-dd'),
      requestedBy: currentUser?.id,
      approvedBy: payload.approvedBy || currentUser?.id,
      originLocationId: payload.originLocationId,
      destinationLocationId: payload.destinationLocationId,
      expectedDelivery: payload.expectedDelivery,
      responsible: payload.responsible,
      parkId: payload.parkId || null,
      areaId: payload.areaId || null,
      costCenterId: payload.costCenterId || null,
      items: payload.items || [],
      documents: [],
      history: [
        {
          id: generateId('SHP-HIST'),
          action: 'Despacho creado',
          status: 'en tránsito',
          userId: currentUser?.id,
          timestamp: now(),
          note: payload.notes || ''
        }
      ]
    };

    (payload.items || []).forEach((item) => {
      adjustInventory(item.sku, payload.originLocationId, -item.quantity);
      registerMovement({
        type: 'salida',
        sku: item.sku,
        quantity: item.quantity,
        locationId: payload.originLocationId,
        reference,
        userId: currentUser?.id,
        parkId: payload.parkId,
        areaId: payload.areaId,
        costCenterId: payload.costCenterId
      });
    });

    setShipments((prev) => [newShipment, ...prev]);
    addAuditRecord('Creación de despacho', reference, 'En tránsito');
    pushNotification({
      title: t('Nuevo despacho creado'),
      message: t(
        'Se creó el despacho {0} hacia {1}.',
        reference,
        destination?.name || 'destino'
      ),
      severity: 'medium'
    });

    return newShipment;
  };

  const markShipmentReceived = (shipmentId, note = '') => {
    setShipments((prev) =>
      prev.map((shipment) => {
        if (shipment.id !== shipmentId) return shipment;
        const updatedShipment = {
          ...shipment,
          status: 'recepcionado',
          receivedAt: format(new Date(), 'yyyy-MM-dd'),
          history: [
            {
              id: generateId('SHP-HIST'),
              action: 'Recepción conforme',
              status: 'recepcionado',
              userId: currentUser?.id,
              timestamp: now(),
              note
            },
            ...(shipment.history || [])
          ]
        };
        updatedShipment.items.forEach((item) => {
          adjustInventory(item.sku, shipment.destinationLocationId, item.quantity);
          registerMovement({
            type: 'recepción',
            sku: item.sku,
            quantity: item.quantity,
            locationId: shipment.destinationLocationId,
            reference: shipment.reference,
            userId: currentUser?.id,
            parkId: shipment.parkId,
            areaId: shipment.areaId,
            costCenterId: shipment.costCenterId
          });
        });

        return updatedShipment;
      })
    );
    addAuditRecord('Recepción despacho', shipmentId, 'Conforme');
    pushNotification({
      title: t('Despacho recepcionado'),
      message: t('El despacho {0} fue marcado como recepcionado.', shipmentId),
      severity: 'info'
    });
  };

  const uploadSupplierDocument = (supplierId, documentName) => {
    setSuppliers((prev) =>
      prev.map((supplier) => {
        if (supplier.id !== supplierId) return supplier;
        return {
          ...supplier,
          documents: [
            { id: generateId('DOC'), name: documentName, uploadedAt: format(new Date(), 'yyyy-MM-dd') },
            ...(supplier.documents || [])
          ]
        };
      })
    );
    addAuditRecord('Carga documento proveedor', supplierId, documentName);
  };

  const updateSupplierCompliance = (supplierId, { complianceScore, riskLevel, checklistUpdates }) => {
    setSuppliers((prev) =>
      prev.map((supplier) => {
        if (supplier.id !== supplierId) return supplier;
        return {
          ...supplier,
          complianceScore: complianceScore ?? supplier.complianceScore,
          riskLevel: riskLevel || supplier.riskLevel,
          complianceChecklist: {
            ...supplier.complianceChecklist,
            ...(checklistUpdates || {})
          }
        };
      })
    );
    addAuditRecord('Actualización compliance proveedor', supplierId, riskLevel || 'Sin cambios');
  };

  const evaluateSupplier = (supplierId, evaluationPayload) => {
    setSuppliers((prev) =>
      prev.map((supplier) => {
        if (supplier.id !== supplierId) return supplier;
        const nextEvaluation = {
          ...supplier.evaluation,
          ...evaluationPayload,
          reviewerId: currentUser?.id || supplier.evaluation?.reviewerId || null,
          lastUpdated: now()
        };
        return {
          ...supplier,
          evaluation: nextEvaluation
        };
      })
    );
    addAuditRecord(
      'Evaluación proveedor',
      supplierId,
      evaluationPayload?.viability || 'Sin decisión'
    );
  };

  const reviewSupplier = (supplierId, { stage, decision, note = '' }) => {
    const targetSupplier = suppliers.find((supplier) => supplier.id === supplierId);
    if (!targetSupplier) return;

    if (stage === 'purchasing' && (targetSupplier.reviews?.contract?.status || 'pendiente') !== 'aprobado') {
      return;
    }

    const timestamp = now();
    const approved = decision === 'approved';
    let nextStatus = targetSupplier.status || 'Pending Legal';
    let statusLabel = '';
    let nextReviews = targetSupplier.reviews || {
      contract: { ...defaultReviewNode },
      purchasing: { ...defaultReviewNode }
    };

    if (stage === 'contract') {
      nextReviews = {
        ...nextReviews,
        contract: {
          status: approved ? 'aprobado' : 'rechazado',
          reviewerId: currentUser?.id || nextReviews.contract.reviewerId,
          date: timestamp,
          note
        }
      };
      nextStatus = approved ? 'Pending Approval' : 'Rejected';
      statusLabel = approved ? 'Revisión legal aprobada' : 'Revisión legal rechazada';
      if (!approved) {
        nextReviews = {
          ...nextReviews,
          purchasing: { ...defaultReviewNode }
        };
      }
    } else if (stage === 'purchasing') {
      nextReviews = {
        ...nextReviews,
        purchasing: {
          status: approved ? 'aprobado' : 'rechazado',
          reviewerId: currentUser?.id || nextReviews.purchasing.reviewerId,
          date: timestamp,
          note
        }
      };
      nextStatus = approved ? 'Approved' : 'Rejected';
      statusLabel = approved ? 'Aprobación gerencia compras' : 'Rechazo gerencia compras';
    } else {
      return;
    }

    setSuppliers((prev) =>
      prev.map((supplier) =>
        supplier.id === supplierId
          ? {
              ...supplier,
              status: nextStatus,
              lastReviewedBy: currentUser?.id || supplier.lastReviewedBy || null,
              reviewedDate: timestamp,
              reviews: nextReviews,
              history: [
                {
                  id: generateId('HIST'),
                  action: statusLabel || 'Revisión proveedor',
                  status: nextStatus,
                  userId: currentUser?.id,
                  timestamp,
                  note
                },
                ...supplier.history
              ]
            }
          : supplier
      )
    );

    addAuditRecord(
      stage === 'purchasing'
        ? approved
          ? 'Aprobación proveedor'
          : 'Rechazo proveedor'
        : approved
        ? 'Revisión legal proveedor'
        : 'Rechazo legal proveedor',
      `${targetSupplier.name || supplierId} · ${stage}`,
      approved ? 'Aprobado' : 'Rechazado'
    );

    const notificationTitle = (() => {
      if (stage === 'contract') {
        return approved
          ? `Documentación de ${targetSupplier.name} validada`
          : `Documentación de ${targetSupplier.name} rechazada`;
      }
      return approved
        ? `Proveedor ${targetSupplier.name} aprobado`
        : `Proveedor ${targetSupplier.name} rechazado`;
    })();

    pushNotification({
      title: notificationTitle,
      message:
        note ||
        (stage === 'contract'
          ? approved
            ? t('Lista para aprobación final de Compras.')
            : t('Se rechazó la documentación legal presentada.')
          : approved
          ? t('Proveedor homologado y disponible para contratación.')
          : t('Proveedor no homologado; revisar observaciones.')),
      severity: approved ? 'info' : 'medium'
    });
  };

  const importMaterialsFromCsv = (rows) => {
    const importedItems = rows.map((row) => {
      const categoryIdFromRow = row.categoriaId || row.CategoriaId || row.categoryId;
      const categoryNameFromRow = row.categoria || row.Categoria || row.category;
      const categoryMatch =
        categories.find(
          (candidate) =>
            candidate.id === categoryIdFromRow ||
            candidate.name.toLowerCase() === (categoryNameFromRow || '').toLowerCase()
        ) || null;

      return {
        id: generateId('ITEM'),
        sku: row.sku || row.SKU,
        description: row.descripcion || row.Descripcion || row.description || '',
        categoryId: categoryMatch?.id || categoryIdFromRow || null,
        category: categoryMatch?.name || categoryNameFromRow || 'General',
        unit: row.unidad || row.Unidad || 'unidad',
        unitValue: Number(row.valorUnitario || row.UnitValue || 0),
        safetyStock: Number(row.stockSeguridad || row.SafetyStock || 0),
        supplierId: row.proveedorId || row.SupplierId || null,
        primaryParkId: row.parqueId || row.ParkId || null,
        locations: [
          {
            locationId: row.ubicacionId || row.LocationId || locations[0]?.id,
            quantity: Number(row.cantidad || row.Quantity || 0)
          }
        ],
        lastMovementAt: format(new Date(), 'yyyy-MM-dd')
      };
    });

    setInventory((prev) => [...importedItems, ...prev]);
    addAuditRecord('Importación de materiales', `${importedItems.length} ítems`, 'Carga masiva');
  };

  const importSuppliersFromCsv = (rows) => {
    const importedSuppliers = rows.map((row) =>
      applySupplierDefaults({
        id: generateId('SUP'),
        name: row.nombre || row.Nombre,
        rut: row.rut || row.RUT,
        country: row.pais || row.Pais || 'Chile',
        type: row.tipo || row.Tipo || 'nacional',
        complianceScore: Number(row.compliance || row.Compliance || 50),
        riskLevel: row.riesgo || row.Riesgo || 'Medio',
        status: 'Pending Legal',
        legal: {
          razonSocial: row.razonSocial || row.RazonSocial || row.nombre || '',
          fechaConstitucion: row.fechaConstitucion || row.FechaConstitucion || '',
          giro: row.giro || row.Giro || ''
        },
        administrative: {
          contacto: row.contacto || row.Contacto || '',
          correo: row.correo || row.Correo || '',
          direccion: row.direccion || row.Direccion || '',
          pais: row.pais || row.Pais || 'Chile',
          telefono: row.telefono || row.Telefono || ''
        },
        financial: {
          tipoEmpresa: row.tipoEmpresa || row.TipoEmpresa || '',
          capital: Number(row.capital || row.Capital || 0),
          deudasPublicas: row.deudasPublicas || row.DeudasPublicas || 'Sin información'
        },
        certifications: [],
        complianceChecklist: {
          tributariaVigente: true,
          sinJuiciosSanciones: true,
          seguridadIndustrial: true,
          sinAntecedentesCorrupcion: true
        },
        documents: [],
        history: []
      })
    );

    setSuppliers((prev) => [...importedSuppliers, ...prev]);
    addAuditRecord('Importación proveedores', `${importedSuppliers.length} proveedores`, 'Carga masiva');
  };

  const appendRfQHistoryEntry = (status, action, note = '', meta = {}) => ({
    id: generateId('RFQH'),
    status,
    action,
    userId: meta.userId || currentUser?.id || null,
    timestamp: now(),
    note,
    details: meta.details || null
  });

  const updateRfQState = (rfqId, reducer) => {
    let snapshot = null;
    setRfqs((prev) =>
      prev.map((rfq) => {
        if (rfq.id !== rfqId) return rfq;
        const next = reducer(rfq);
        snapshot = next;
        return next;
      })
    );
    return snapshot;
  };

  const createRfQ = (rfqData, { submit = false } = {}) => {
    const rfqId = rfqData.id || generateId('RFQ');
    const createdDate = rfqData.createdDate || format(new Date(), 'yyyy-MM-dd');
    const baseLines = Array.isArray(rfqData.lines)
      ? rfqData.lines.map((line) => ({
          id: line.id || generateId('RFQLN'),
          description: line.description || '',
          quantity: Number(line.quantity || 0),
          unit: line.unit || 'unidad',
          requestedDate: line.requestedDate || null
        }))
      : [];

    const baseHistory = [
      appendRfQHistoryEntry('draft', 'Creación RFQ', rfqData.note || rfqData.description || '')
    ];
    if (submit) {
      baseHistory.unshift(
        appendRfQHistoryEntry(
          'received',
          'Recepción Compras',
          'RFQ enviada automáticamente a compras.'
        )
      );
    }

    const newRfQ = {
      id: rfqId,
      title: rfqData.title || t('RFQ sin título'),
      requesterId: rfqData.requesterId || currentUser?.id || null,
      requesterName: rfqData.requesterName || currentUser?.name || '',
      parkId: rfqData.parkId || null,
      costCenterId: rfqData.costCenterId || null,
      createdDate,
      status: submit ? 'received' : 'draft',
      buyerId: null,
      buyerName: null,
      supplierId: null,
      supplierName: null,
      linkedTenderId: rfqData.linkedTenderId || null,
      linkedOrderId: null,
      expectedDeliveryDate: rfqData.expectedDeliveryDate || null,
      lines: baseLines,
      history: baseHistory
    };

    setRfqs((prev) => [newRfQ, ...prev]);
    addAuditRecord('Creación RFQ', rfqId, newRfQ.status);
    pushNotification({
      title: submit ? t('RFQ {0} enviada', rfqId) : t('RFQ {0} creada', rfqId),
      message: submit
        ? t('Compras recibió la solicitud para revisión.')
        : t('La RFQ quedó guardada en borrador.'),
      severity: submit ? 'medium' : 'info'
    });
    return newRfQ;
  };

  const receiveRfQInPurchasing = (rfqId, note = '') => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'received',
        history: [appendRfQHistoryEntry('received', 'Recepción Compras', note), ...(rfq.history || [])]
      };
    });
    if (updated) {
      addAuditRecord('RFQ recepción', rfqId, 'received');
      pushNotification({
        title: t('RFQ {0} recibida', updated.id),
        message: t('Queda pendiente validación de completitud.'),
        severity: 'info'
      });
    }
    return updated;
  };

  const validateRfQCompleteness = (
    rfqId,
    { complete, note = '' } = {}
  ) => {
    const nextStatus = complete ? 'ready_to_assign' : 'needs_correction';
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: nextStatus,
        history: [
          appendRfQHistoryEntry(
            nextStatus,
            complete ? 'RFQ validada' : 'RFQ devuelta por ajustes',
            note
          ),
          ...(rfq.history || [])
        ]
      };
    });
    if (updated) {
      addAuditRecord('RFQ validación', rfqId, nextStatus);
      pushNotification({
        title: t('Validación RFQ {0}', updated.id),
        message: complete
          ? t('Lista para asignar comprador.')
          : t('Se solicitó corrección al solicitante.'),
        severity: complete ? 'info' : 'medium'
      });
    }
    return updated;
  };

  const requestRfQCorrection = (rfqId, note = '') =>
    validateRfQCompleteness(rfqId, { complete: false, note });

  const assignRfQBuyer = (rfqId, buyerId, note = '') => {
    const buyer = buyerId ? findUserById(buyerId) : null;
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'assigned',
        buyerId,
        buyerName: buyer?.name || rfq.buyerName,
        history: [appendRfQHistoryEntry('assigned', 'Asignación de comprador', note), ...(rfq.history || [])]
      };
    });
    if (updated) {
      addAuditRecord('RFQ asignación', rfqId, 'assigned');
      pushNotification({
        title: t('RFQ {0} asignada', updated.id),
        message: buyer
          ? t('{0} gestionará la licitación.', buyer.name)
          : t('RFQ asignada a compras.'),
        severity: 'info'
      });
    }
    return updated;
  };

  const launchRfQQuotation = (rfqId, { tenderId = null, note = '' } = {}) => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'in_quotation',
        linkedTenderId: tenderId || rfq.linkedTenderId,
        history: [appendRfQHistoryEntry('in_quotation', 'Inicio proceso de cotización', note), ...(rfq.history || [])]
      };
    });
    if (updated) {
      addAuditRecord('RFQ licitación', rfqId, 'in_quotation');
      pushNotification({
        title: t('RFQ {0} en licitación', updated.id),
        message: t('Se invitó a proveedores a cotizar.'),
        severity: 'info'
      });
    }
    return updated;
  };

  const selectRfQPreferredSupplier = (
    rfqId,
    { supplierId, supplierName = '', note = '' } = {}
  ) => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'evaluating',
        supplierId: supplierId || rfq.supplierId,
        supplierName: supplierName || rfq.supplierName,
        history: [
          appendRfQHistoryEntry('evaluating', 'Evaluación y selección de proveedor', note),
          ...(rfq.history || [])
        ]
      };
    });
    if (updated) {
      addAuditRecord('RFQ evaluación proveedor', rfqId, updated.status);
      pushNotification({
        title: t('RFQ {0} en evaluación', updated.id),
        message: supplierName
          ? t('Se analiza la propuesta de {0}.', supplierName)
          : t('Se inició la comparativa de ofertas recibidas.'),
        severity: 'info'
      });
    }
    return updated;
  };

  const generateRfQPurchaseOrder = (rfqId, orderData = {}) => {
    const rfq = rfqs.find((item) => item.id === rfqId);
    if (!rfq || rfq.status === 'cancelled') return null;
    const order = createPurchaseOrder({
      supplierId: orderData.supplierId || rfq.supplierId,
      supplierName: orderData.supplierName || rfq.supplierName,
      parkId: orderData.parkId || rfq.parkId,
      costCenterId: orderData.costCenterId || rfq.costCenterId,
      notes: orderData.notes || `Generada desde RFQ ${rfq.id}.` ,
      items:
        orderData.items && orderData.items.length > 0
          ? orderData.items
          : (rfq.lines || []).map((line) => ({
              sku: line.sku || null,
              description: line.description,
              quantity: line.quantity,
              unit: line.unit || 'unidad',
              unitPrice: line.unitPrice || 0
            })),
      deliveryDate: orderData.deliveryDate || rfq.expectedDeliveryDate,
      attachments: orderData.attachments || [],
      type: orderData.type || 'nacional',
      currency: orderData.currency || 'CLP',
      areaId: orderData.areaId || null,
      projectManagerId: orderData.projectManagerId || null,
      categoryId: orderData.categoryId || null
    });

    if (!order) return null;

    const updated = updateRfQState(rfqId, (current) => ({
      ...current,
      status: 'po_generated',
      linkedOrderId: order.id,
      history: [
        appendRfQHistoryEntry('po_generated', 'Generación de PO', orderData.note || ''),
        ...(current.history || [])
      ]
    }));

    if (updated) {
      addAuditRecord('RFQ generación PO', rfqId, 'po_generated');
      pushNotification({
        title: t('RFQ {0} generó OC {1}', updated.id, order.id),
        message: t('La orden inicia el flujo de aprobación.'),
        severity: 'info'
      });
    }

    return order;
  };

  const markRfQPoForApproval = (rfqId, note = '') => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'po_pending_approval',
        history: [
          appendRfQHistoryEntry('po_pending_approval', 'PO enviada a aprobación', note),
          ...(rfq.history || [])
        ]
      };
    });
    if (updated) {
      addAuditRecord('RFQ PO en aprobación', rfqId, 'po_pending_approval');
    }
    return updated;
  };

  const recordRfQPoDecision = (rfqId, approved, note = '') => {
    const nextStatus = approved ? 'po_approved' : 'po_adjustment';
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: nextStatus,
        history: [
          appendRfQHistoryEntry(
            nextStatus,
            approved ? 'PO aprobada' : 'PO rechazada / ajuste requerido',
            note
          ),
          ...(rfq.history || [])
        ]
      };
    });
    if (updated) {
      addAuditRecord('RFQ decisión PO', rfqId, nextStatus);
      pushNotification({
        title: `PO ${updated.linkedOrderId || ''} ${approved ? 'aprobada' : 'con ajustes'}`,
        message: note || (approved ? t('Se notificó a compras para envío.') : t('Revisar ajustes solicitados.')),
        severity: approved ? 'info' : 'medium'
      });
    }
    return updated;
  };

  const markRfQPoSent = (rfqId, note = '') => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'po_sent',
        history: [appendRfQHistoryEntry('po_sent', 'PO enviada al proveedor', note), ...(rfq.history || [])]
      };
    });
    if (updated) {
      addAuditRecord('RFQ envío PO', rfqId, 'po_sent');
      pushNotification({
        title: t('PO {0} enviada', updated.linkedOrderId || ''),
        message: t('Se notificó al proveedor y al solicitante.'),
        severity: 'info'
      });
    }
    return updated;
  };

  const closeRfQ = (rfqId, note = '') => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq || rfq.status === 'cancelled') return rfq;
      return {
        ...rfq,
        status: 'closed',
        history: [appendRfQHistoryEntry('closed', 'RFQ cerrada', note), ...(rfq.history || [])]
      };
    });
    if (updated) {
      addAuditRecord('RFQ cerrada', rfqId, 'closed');
    }
    return updated;
  };

  const cancelRfQ = (rfqId, note = '') => {
    const updated = updateRfQState(rfqId, (rfq) => {
      if (!rfq) return rfq;
      return {
        ...rfq,
        status: 'cancelled',
        history: [appendRfQHistoryEntry('cancelled', 'RFQ cancelada', note), ...(rfq.history || [])]
      };
    });
    if (updated) {
      addAuditRecord('RFQ cancelada', rfqId, 'cancelled');
      pushNotification({
        title: t('RFQ {0} cancelada', rfqId),
        message: note || t('Se notificó al equipo solicitante.'),
        severity: 'medium'
      });
    }
    return updated;
  };

  const createTender = (tenderData) => {
    const tenderId = generateId('TND');
    const creationDate = format(new Date(), 'yyyy-MM-dd');
    const tender = {
      id: tenderId,
      tenderNumber: tenderData.tenderNumber || null,
      title: tenderData.title || t('Nueva licitación'),
      scope: tenderData.scope || '',
      createdDate: creationDate,
      closingDate: tenderData.closingDate || null,
      status: 'pending_id',
      createdBy: tenderData.createdBy || currentUser?.id || null,
      controllerId: tenderData.controllerId || null,
      relatedRequisitionId: tenderData.relatedRequisitionId || null,
      projectId: tenderData.projectId || null,
      estimatedValue: Number(tenderData.estimatedValue || 0),
      currency: tenderData.currency || 'CLP',
      offers: Array.isArray(tenderData.offers) ? recalculateOfferScores(tenderData.offers) : [],
      awardedOfferId: null,
      attachments: normalizeAttachmentList(tenderData.attachments || []),
      approvals: getDefaultTenderApprovals(users),
      generatedOrderId: null,
      history: [
        {
          id: generateId('TNDH'),
          action: 'Creación licitación',
          userId: currentUser?.id || tenderData.createdBy || null,
          timestamp: now(),
          note: tenderData.scope || 'Licitación creada desde módulo de compras.'
        }
      ]
    };

    setTenders((prev) => [tender, ...prev]);
    if (tender.relatedRequisitionId) {
      setPurchaseRequisitions((prev) =>
        prev.map((req) =>
          req.id === tender.relatedRequisitionId
            ? {
                ...req,
                tenderId,
                status: 'en_licitacion',
                history: [
                  {
                    id: generateId('HIST'),
                    action: 'En licitación',
                    status: 'en_licitacion',
                    userId: currentUser?.id || tender.createdBy || null,
                    timestamp: now(),
                    note: `Licitación ${tenderId} creada.`
                  },
                  ...(req.history || [])
                ]
              }
            : req
        )
      );
    }
    pushNotification({
      title: t('Nueva licitación creada'),
      message: t('{0} espera asignación de ID por Controller.', tender.title),
      severity: 'medium'
    });
    return tender;
  };

  const addTenderOffer = (tenderId, offerData) => {
    setTenders((prev) =>
      prev.map((tender) => {
        if (tender.id !== tenderId) return tender;
        const newOffer = {
          id: generateId('TNOF'),
          supplierId: offerData.supplierId || null,
          supplierName: offerData.supplierName || 'Proveedor invitado',
          amount: Number(offerData.amount || 0),
          currency: offerData.currency || tender.currency || 'CLP',
          deliveryTime: Number(offerData.deliveryTime || 0),
          lineItems: Array.isArray(offerData.lineItems) ? offerData.lineItems : [],
          notes: offerData.notes || '',
          createdAt: now()
        };
        const updatedOffers = recalculateOfferScores([...tender.offers, newOffer]);
        return {
          ...tender,
          status: tender.tenderNumber ? 'in_evaluation' : 'pending_id',
          offers: updatedOffers,
          history: [
            {
              id: generateId('TNDH'),
              action: 'Oferta registrada',
              userId: currentUser?.id || offerData.userId || null,
              timestamp: now(),
              note: `${newOffer.supplierName} ofertó ${Number(newOffer.amount || 0).toLocaleString('es-CL')} ${newOffer.currency}.`
            },
            ...tender.history
          ]
        };
      })
    );
  };

  const addTenderAttachments = (tenderId, attachments = []) => {
    const normalized = normalizeAttachmentList(attachments);
    if (normalized.length === 0) return;
    setTenders((prev) =>
      prev.map((tender) => {
        if (tender.id !== tenderId) return tender;
        return {
          ...tender,
          attachments: [...normalized, ...(tender.attachments || [])],
          history: [
            {
              id: generateId('TNDH'),
              action: 'Documentos adjuntos',
              userId: currentUser?.id || null,
              timestamp: now(),
              note: `${normalized.length} archivo(s) agregados.`
            },
            ...tender.history
          ]
        };
      })
    );
  };

  const assignTenderId = (tenderId) => {
    let generatedCode = null;
    setTenders((prev) => {
      const code = computeNextTenderCode(prev);
      generatedCode = code;
      return prev.map((tender) => {
        if (tender.id !== tenderId) return tender;
        return {
          ...tender,
          tenderNumber: code,
          controllerId: currentUser?.id || tender.controllerId,
          status: tender.offers.length > 0 ? 'in_evaluation' : 'pending_id',
          history: [
            {
              id: generateId('TNDH'),
              action: 'ID asignado',
              userId: currentUser?.id || null,
              timestamp: now(),
              note: `Se asignó folio ${code}.`
            },
            ...tender.history
          ]
        };
      });
    });

    if (generatedCode) {
      pushNotification({
        title: t('Folio de licitación asignado'),
        message: t('La licitación recibió el código {0}.', generatedCode),
        severity: 'info'
      });
    }

    return generatedCode;
  };

  const awardTender = (tenderId, offerId) => {
    let awardedSupplierName = null;
    setTenders((prev) =>
      prev.map((tender) => {
        if (tender.id !== tenderId) return tender;
        const winningOffer = tender.offers.find((offer) => offer.id === offerId);
        if (!winningOffer) return tender;
        awardedSupplierName = winningOffer.supplierName;
        return {
          ...tender,
          status: 'awarded',
          awardedOfferId: offerId,
          history: [
            {
              id: generateId('TNDH'),
              action: 'Adjudicación',
              userId: currentUser?.id || null,
              timestamp: now(),
              note: `Se adjudicó a ${winningOffer.supplierName}.`
            },
            ...tender.history
          ]
        };
      })
    );

    pushNotification({
      title: t('Licitación adjudicada'),
      message: awardedSupplierName
        ? t('Proveedor seleccionado: {0}.', awardedSupplierName)
        : t('Se registró la adjudicación de la licitación.'),
      severity: 'high'
    });
  };

  const updateTenderApproval = (tenderId, stage, decision = true, note = '') => {
    const stageMap = {
      project: 'projectManager',
      purchasing: 'purchasingManager'
    };
    const labelMap = {
      project: 'Project Manager',
      purchasing: 'Gerencia de Compras'
    };
    const stageKey = stageMap[stage];
    if (!stageKey) return;

    let generatedOrderData = null;
    let approvalNotification = null;

    setTenders((prev) =>
      prev.map((tender) => {
        if (tender.id !== tenderId) return tender;
        const approvalNode = tender.approvals?.[stageKey] || {};
        if (approvalNode.status && approvalNode.status !== 'pendiente') {
          return tender;
        }
        const assignedApproverId = approvalNode.userId || null;
        const canAct =
          currentUser?.role === 'super_user' ||
          (assignedApproverId && currentUser?.id && currentUser.id === assignedApproverId);
        if (!canAct) {
          return tender;
        }

        const decisionStatus = decision ? 'aprobada' : 'rechazada';
        const decisionDate = format(new Date(), 'yyyy-MM-dd');
        const updatedApprovals = {
          ...tender.approvals,
          [stageKey]: {
            ...approvalNode,
            status: decisionStatus,
            date: decisionDate,
            note,
            userId: currentUser?.id || approvalNode.userId
          }
        };

        let status = tender.status;
        let generatedOrderId = tender.generatedOrderId || null;
        const historyEntries = [
          {
            id: generateId('TNDH'),
            action: decision
              ? `Aprobación ${labelMap[stage]}`
              : `Rechazo ${labelMap[stage]}`,
            userId: currentUser?.id || null,
            timestamp: now(),
            note
          }
        ];

        if (!decision) {
          status = 'rechazada';
        } else {
          if (
            stage === 'purchasing' &&
            tender.relatedRequisitionId &&
            decision &&
            !tender.awardedOfferId
          ) {
            status = tender.status || 'in_evaluation';
          }
          const pmApproved = updatedApprovals.projectManager?.status === 'aprobada';
          const gmApproved = updatedApprovals.purchasingManager?.status === 'aprobada';
          if (pmApproved && gmApproved && !generatedOrderId) {
            const preferredOffer = getPreferredOffer(tender);
            if (preferredOffer && tender.relatedRequisitionId) {
              const order = convertRequisitionToOrder(tender.relatedRequisitionId, {
                supplierId: preferredOffer.supplierId || null,
                supplierName: preferredOffer.supplierName,
                totalValue: preferredOffer.amount,
                tenderId: tender.id,
                items: Array.isArray(preferredOffer.lineItems) && preferredOffer.lineItems.length > 0
                  ? preferredOffer.lineItems.map((item) => ({
                      sku: item.sku || null,
                      description: item.description,
                      unit: item.unit || 'unidad',
                      quantity: item.quantity,
                      unitPrice: item.unitPrice || 0
                    }))
                  : [],
                attachments: tender.attachments
              });
              if (order) {
                generatedOrderId = order.id;
                status = 'converted_to_order';
                historyEntries.unshift({
                  id: generateId('TNDH'),
                  action: 'OC generada automáticamente',
                  userId: currentUser?.id || null,
                  timestamp: now(),
                  note: `Se creó la orden ${order.id}`
                });
                generatedOrderData = {
                  orderId: order.id,
                  tenderTitle: tender.title
                };
              } else {
                historyEntries.unshift({
                  id: generateId('TNDH'),
                  action: 'Intento de generación de OC',
                  userId: currentUser?.id || null,
                  timestamp: now(),
                  note: 'No fue posible generar la orden automáticamente; revisa el estado de la requisición vinculada.'
                });
              }
            }
          } else if (status === 'pending_id' || status === 'in_evaluation') {
            status = tender.tenderNumber ? 'in_evaluation' : 'pending_id';
          }
        }

        approvalNotification = {
          title: decision
            ? t('Aprobación {0}', labelMap[stage])
            : t('Rechazo {0}', labelMap[stage]),
          message: decision
            ? t(
            '{0} aprobó la comparativa{1}.',
            currentUser?.name || 'Aprobador',
            note ? ` · ${note}` : ''
          )
            : t(
            '{0} rechazó la comparativa{1}.',
            currentUser?.name || 'Aprobador',
            note ? ` · ${note}` : ''
          ),
          severity: decision ? 'info' : 'medium'
        };

        return {
          ...tender,
          approvals: updatedApprovals,
          status,
          generatedOrderId,
          history: [...historyEntries, ...tender.history]
        };
      })
    );

    if (approvalNotification) {
      pushNotification(approvalNotification);
    }
    if (generatedOrderData) {
      pushNotification({
        title: t('OC generada desde licitación'),
        message: t(
          'Se creó la orden {0} para {1}.',
          generatedOrderData.orderId,
          generatedOrderData.tenderTitle
        ),
        severity: 'high'
      });
    }
  };

  const markNotificationAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId ? { ...notification, read: true } : notification
      )
    );
  };

  const removeNotification = (notificationId) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== notificationId));
  };

  const addUser = (userData) => {
    const role = userData.role || 'comprador';
    const newUser = {
      id: generateId('USR'),
      name: userData.name,
      email: userData.email,
      password: userData.password || DEFAULT_PASSWORD,
      role,
      profile: userData.profile || PROFILE_FALLBACK_BY_ROLE[role] || 'operativo',
      avatar: userData.avatar || userData.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase(),
      department: userData.department || 'Sin asignar',
      phone: userData.phone || '',
      lastLogin: format(new Date(), 'yyyy-MM-dd'),
      permissions: userData.permissions || [],
      allowedPages: Array.isArray(userData.allowedPages) ? userData.allowedPages : null
    };
    setUsers((prev) => [newUser, ...prev]);
    addAuditRecord('Alta de usuario', newUser.email, 'Creado');
    return newUser;
  };

  const updateUserRole = (userId, role) => {
    setUsers((prev) =>
      prev.map((user) => (user.id === userId ? { ...user, role, profile: PROFILE_FALLBACK_BY_ROLE[role] || user.profile || 'operativo' } : user))
    );
    setCurrentUser((prev) =>
      prev && prev.id === userId
        ? { ...prev, role, profile: PROFILE_FALLBACK_BY_ROLE[role] || prev.profile || 'operativo' }
        : prev
    );
    addAuditRecord('Actualización de rol', userId, role);
  };

  const updateUserProfile = (userId, profile) => {
    if (!userId || !profile) return;
    const mappedRole = PRIMARY_ROLE_BY_PROFILE[profile] || 'comprador';
    setUsers((prev) =>
      prev.map((user) => (user.id === userId ? { ...user, profile, role: mappedRole } : user))
    );
    setCurrentUser((prev) => (prev && prev.id === userId ? { ...prev, profile, role: mappedRole } : prev));
    addAuditRecord('Actualización de perfil', userId, profile);
  };

  const updateUserAllowedPages = (userId, pages) => {
    if (!userId) return;
    const safePages = Array.isArray(pages) && pages.length > 0 ? pages : null;
    setUsers((prev) =>
      prev.map((user) => (user.id === userId ? { ...user, allowedPages: safePages } : user))
    );
    setCurrentUser((prev) => (prev && prev.id === userId ? { ...prev, allowedPages: safePages } : prev));
    addAuditRecord('Actualización accesos módulos', userId, safePages ? `${safePages.length} páginas` : 'Default por perfil');
  };

  const tenderRequisition = (requisitionId, tenderDetails) => {
    setPurchaseRequisitions((prev) =>
      prev.map((req) =>
        req.id === requisitionId
          ? {
              ...req,
              tenderDetails,
              status: 'en_licitacion',
              history: [
                ...req.history,
                {
                  id: generateId('HIST'),
                  action: 'Tendered',
                  status: 'en_licitacion',
                  userId: currentUser.id,
                  timestamp: now(),
                  note: 'Requisition tendered.'
                }
              ]
            }
          : req
      )
    );
  };

  const approvePurchaseOrder = (orderId, approverId) => {
    setPurchaseOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? {
              ...order,
              status: 'Approved',
              history: [
                ...order.history,
                {
                  id: generateId('HIST'),
                  action: 'Approved',
                  status: 'Approved',
                  userId: approverId,
                  timestamp: now(),
                  note: 'Purchase order approved.'
                }
              ]
            }
          : order
      )
    );
  };

  const addSupplier = (supplier) => {
    const supplierWithMetadata = applySupplierDefaults({
      ...supplier,
      id: generateId('SUP'),
      status: 'Pending Legal',
      createdBy: currentUser.id,
      createdDate: now(),
      history: [
        {
          id: generateId('HIST'),
          action: 'Created',
          status: 'Pending Legal',
          userId: currentUser.id,
          timestamp: now(),
          note: 'Supplier created.'
        },
        ...(supplier.history || [])
      ]
    });
    setSuppliers((prev) => [supplierWithMetadata, ...prev]);
  };

  const approveSupplier = (supplierId, approverId) => {
    setSuppliers((prev) =>
      prev.map((supplier) =>
        supplier.id === supplierId
          ? {
              ...supplier,
              status: 'Approved',
              reviews: {
                contract: {
                  status: 'aprobado',
                  reviewerId: supplier.reviews?.contract?.reviewerId || approverId,
                  date: supplier.reviews?.contract?.date || now(),
                  note: supplier.reviews?.contract?.note || ''
                },
                purchasing: {
                  status: 'aprobado',
                  reviewerId: approverId,
                  date: now(),
                  note: 'Aprobado manualmente'
                }
              },
              history: [
                ...supplier.history,
                {
                  id: generateId('HIST'),
                  action: 'Approved',
                  status: 'Approved',
                  userId: approverId,
                  timestamp: now(),
                  note: 'Supplier approved.'
                }
              ]
            }
          : supplier
      )
    );
  };

  const createLegalCase = useCallback(
    (caseData) => {
      if (!caseData) return null;
      const timestamp = now();
      const newCase = {
        id: caseData.id || generateId('CASE'),
        title: caseData.title || t('Nuevo caso legal'),
        type: caseData.type || 'Otros',
        caseNumber: caseData.caseNumber || '',
        tribunal: caseData.tribunal || '',
        counterparty: caseData.counterparty || '',
        status: caseData.status || 'En evaluación',
        stage: caseData.stage || 'Investigación',
        riskLevel: caseData.riskLevel || 'Medio',
        nextDeadline: caseData.nextDeadline || null,
        nextDeadlineType: caseData.nextDeadlineType || null,
        assignedLawyers: Array.isArray(caseData.assignedLawyers)
          ? caseData.assignedLawyers
          : [],
        externalCounsel: caseData.externalCounsel || null,
        estimatedCost: Number(caseData.estimatedCost || 0),
        incurredCost: Number(caseData.incurredCost || 0),
        summary: caseData.summary || '',
        milestones: Array.isArray(caseData.milestones) ? caseData.milestones : [],
        documents: Array.isArray(caseData.documents) ? caseData.documents : [],
        createdAt: caseData.createdAt || timestamp,
        updatedAt: timestamp
      };

      setLegalCases((prev) => [newCase, ...prev]);
      addAuditRecord('Alta caso legal', newCase.id, newCase.status);
      return newCase;
    },
    [addAuditRecord]
  );

  const updateLegalCase = useCallback(
    (caseId, updates) => {
      if (!caseId || !updates) return;
      const timestamp = now();
      setLegalCases((prev) =>
        prev.map((legalCase) =>
          legalCase.id === caseId
            ? {
                ...legalCase,
                ...updates,
                assignedLawyers: updates.assignedLawyers
                  ? Array.from(new Set(updates.assignedLawyers))
                  : legalCase.assignedLawyers,
                updatedAt: timestamp
              }
            : legalCase
        )
      );
      addAuditRecord('Actualización caso legal', caseId, updates.status || 'Sin cambio');
    },
    [addAuditRecord]
  );

  const addLegalCaseMilestone = useCallback(
    (caseId, milestone) => {
      if (!caseId || !milestone) return null;
      const enrichedMilestone = {
        id: milestone.id || generateId('CASESTEP'),
        date: milestone.date || format(new Date(), 'yyyy-MM-dd'),
        description: milestone.description || '',
        createdBy: milestone.createdBy || currentUser?.id || null
      };
      let createdMilestone = null;
      setLegalCases((prev) =>
        prev.map((legalCase) => {
          if (legalCase.id !== caseId) return legalCase;
          createdMilestone = enrichedMilestone;
          return {
            ...legalCase,
            milestones: [...(legalCase.milestones || []), enrichedMilestone],
            updatedAt: now()
          };
        })
      );
      if (createdMilestone) {
        addAuditRecord('Milestone caso legal', caseId, enrichedMilestone.description);
      }
      return createdMilestone;
    },
    [addAuditRecord, currentUser?.id]
  );

  const assignLawyersToCase = useCallback(
    (caseId, lawyerIds) => {
      if (!caseId || !Array.isArray(lawyerIds)) return;
      setLegalCases((prev) =>
        prev.map((legalCase) =>
          legalCase.id === caseId
            ? {
                ...legalCase,
                assignedLawyers: Array.from(new Set(lawyerIds)),
                updatedAt: now()
              }
            : legalCase
        )
      );
      addAuditRecord('Asignación caso legal', caseId, `Abogados: ${lawyerIds.join(', ')}`);
    },
    [addAuditRecord]
  );

  const createLegalDocument = useCallback(
    (documentData) => {
      if (!documentData) return null;
      const newDocument = {
        id: documentData.id || generateId('DOCLEGAL'),
        name: documentData.name || 'Documento legal',
        type: documentData.type || 'Otro',
        category: documentData.category || 'General',
        uploadedAt: documentData.uploadedAt || format(new Date(), 'yyyy-MM-dd'),
        owner: documentData.owner || currentUser?.id || null,
        version: documentData.version || 'v1',
        restricted: Boolean(documentData.restricted),
        relatedEntity: documentData.relatedEntity || null,
        notes: documentData.notes || ''
      };
      setLegalDocuments((prev) => [newDocument, ...prev]);
      addAuditRecord('Alta documento legal', newDocument.id, newDocument.type);
      return newDocument;
    },
    [addAuditRecord, currentUser?.id]
  );

  const updateLegalContract = useCallback(
    (contractId, updates) => {
      if (!contractId || !updates) return;
      setLegalContracts((prev) =>
        prev.map((contract) =>
          contract.id === contractId
            ? {
                ...contract,
                ...updates,
                alerts: updates.alerts || contract.alerts,
                tags: updates.tags || contract.tags,
                documents: updates.documents || contract.documents,
                updatedAt: now()
              }
            : contract
        )
      );
      addAuditRecord('Actualización contrato legal', contractId, updates.status || 'Sin cambio');
    },
    [addAuditRecord]
  );

  const upsertLegalComplianceAction = useCallback(
    (areaId, obligationId, updates) => {
      if (!areaId || !obligationId || !updates) return;
      setLegalCompliance((prev) =>
        prev.map((area) => {
          if (area.id !== areaId) return area;
          const obligations = area.obligations || [];
          const obligationExists = obligations.some((item) => item.id === obligationId);
          const updatedObligations = obligationExists
            ? obligations.map((item) =>
                item.id === obligationId ? { ...item, ...updates } : item
              )
            : [...obligations, { id: obligationId, ...updates }];
          return {
            ...area,
            obligations: updatedObligations
          };
        })
      );
      addAuditRecord('Actualización compliance legal', areaId, obligationId);
    },
    [addAuditRecord]
  );

  const updateLegalPower = useCallback(
    (powerId, updates) => {
      if (!powerId || !updates) return;
      setLegalPowers((prev) =>
        prev.map((power) =>
          power.id === powerId
            ? {
                ...power,
                ...updates,
                notes: updates.notes || power.notes
              }
            : power
        )
      );
      addAuditRecord('Actualización poder', powerId, updates.status || 'Sin cambio');
    },
    [addAuditRecord]
  );

  const updateLawyerProfile = useCallback(
    (lawyerId, updates) => {
      if (!lawyerId || !updates) return;
      setLawyers((prev) =>
        prev.map((lawyer) =>
          lawyer.id === lawyerId
            ? {
                ...lawyer,
                ...updates
              }
            : lawyer
        )
      );
      addAuditRecord('Actualización abogado', lawyerId, updates.profile || 'Perfil');
    },
    [addAuditRecord]
  );

  const upsertLegalReportMetric = useCallback((metric) => {
    if (!metric || !metric.id) return;
    setLegalReports((prev) => {
      const exists = prev.some((item) => item.id === metric.id);
      if (exists) {
        return prev.map((item) => (item.id === metric.id ? { ...item, ...metric } : item));
      }
      return [metric, ...prev];
    });
  }, []);

  const value = useMemo(
    () => ({
      currentUser,
      setCurrentUser,
      activePage,
      setActivePage,
      preferences,
      updatePreferences,
      login,
      logout,
      users,
      locations,
      parks,
      costCenters,
      categories,
      resetAppToZero,
      loadEnergyTemplates,
      importSeedData,
      createPark,
      addParkPackage,
      addParkArea,
      updatePark,
      createCategory,
      createRenewableProject,
      addRenewablePermit,
      addDefaultPermitsForRenewableProject,
      projectBudgets,
      projectIncomes,
      setProjectBudgetCurrency,
      projectTimelines,
      projectStatusProjects,
      projectStatusItems,
      enhancedProjectStatusItems,
      projectStatusProjectMap,
      getProjectStatusBundle,
      allProjectsStatus,
      renewableProjects,
      renewablePermits,
      renewableRisks,
      renewableMilestones,
      renewableProjectMap,
      renewablePortfolio,
      renewableProcurementSummary,
      renewablePermitAlerts,
      renewableKpis,
      getRenewableProjectBlockers,
      inventory,
      purchaseOrders,
      purchaseRequisitions,
      shipments,
      movements,
      suppliers,
      notifications,
      auditTrail,
      tenders,
      rfqs,
      apiEndpoints,
      lawyers,
      legalCases,
      legalContracts,
      legalCompliance,
      legalDocuments,
      legalPowers,
      legalReports,
      addUser,
      updateUserRole,
      updateUserProfile,
      updateUserAllowedPages,
      createPurchaseOrder,
      updatePurchaseOrder,
      setPurchaseOrderBudgetIncrementAcceptance,
      advancePurchaseOrderApproval,
      markPurchaseOrderReceived,
      markPurchaseOrderInvoiced,
      markPurchaseOrderPaid,
      markPurchaseOrderSent,
      createPurchaseRequisition,
      submitPurchaseRequisition,
      setRequisitionBudgetOverrunAcceptance,
      updateRequisitionApproval,
      assignRequisitionToBuyer,
      convertRequisitionToOrder,
      createShipment,
      markShipmentReceived,
      registerMovement,
      createTender,
      addTenderOffer,
      assignTenderId,
      awardTender,
      addTenderAttachments,
      updateTenderApproval,
      adjustInventory,
      uploadSupplierDocument,
      updateSupplierCompliance,
      evaluateSupplier,
      reviewSupplier,
      importMaterialsFromCsv,
      importSuppliersFromCsv,
      markNotificationAsRead,
      removeNotification,
      pushNotification,
      addAuditRecord,
      findUserById,
      findParkById,
      findAreaById,
      findCostCenterById,
      findPackageById,
      getProjectBudgetSummary,
      projectFinancialOverview,
      upsertProjectBudgetAllocation,
      removeProjectBudgetAllocation,
      upsertProjectIncome,
      getProjectTimeline,
      addProjectTimelineTask,
      updateProjectStatusItem,
      resetProjectStatusMatrix,
      updateRenewableProjectStage,
      updateRenewablePermit,
      updateRenewableProjectApproval,
      updateRenewableRisk,
      projectStageOrder,
      projectStatusCatalog,
      addSupplier,
      approveSupplier,
      createLegalCase,
      updateLegalCase,
      addLegalCaseMilestone,
      assignLawyersToCase,
      createLegalDocument,
      updateLegalContract,
      upsertLegalComplianceAction,
      updateLegalPower,
      updateLawyerProfile,
      upsertLegalReportMetric
    }),
	    [
	      currentUser,
	      setCurrentUser,
	      activePage,
	      setActivePage,
	      preferences,
	      updatePreferences,
	      users,
	      locations,
	      parks,
      costCenters,
      categories,
      resetAppToZero,
      loadEnergyTemplates,
      importSeedData,
      createPark,
      addParkPackage,
      addParkArea,
      createCategory,
      createRenewableProject,
      addRenewablePermit,
      addDefaultPermitsForRenewableProject,
      projectBudgets,
      projectIncomes,
      projectTimelines,
      projectStatusProjects,
      projectStatusItems,
      enhancedProjectStatusItems,
      projectStatusProjectMap,
      getProjectStatusBundle,
      allProjectsStatus,
      renewableProjects,
      renewablePermits,
      renewableRisks,
      renewableMilestones,
      renewableProjectMap,
      renewablePortfolio,
      renewableProcurementSummary,
      renewablePermitAlerts,
      renewableKpis,
      getRenewableProjectBlockers,
      inventory,
      purchaseOrders,
      purchaseRequisitions,
      shipments,
      movements,
      suppliers,
      notifications,
      auditTrail,
      tenders,
      rfqs,
      apiEndpoints,
      lawyers,
      legalCases,
      legalContracts,
      legalCompliance,
      legalDocuments,
      legalPowers,
      legalReports,
      addUser,
      updateUserRole,
      updateUserProfile,
      updateUserAllowedPages,
      createPurchaseOrder,
      updatePurchaseOrder,
      setPurchaseOrderBudgetIncrementAcceptance,
      advancePurchaseOrderApproval,
      markPurchaseOrderReceived,
      markPurchaseOrderInvoiced,
      markPurchaseOrderPaid,
      markPurchaseOrderSent,
      markPurchaseOrderSent,
      markPurchaseOrderSent,
      createPurchaseRequisition,
      submitPurchaseRequisition,
      setRequisitionBudgetOverrunAcceptance,
      updateRequisitionApproval,
      assignRequisitionToBuyer,
      convertRequisitionToOrder,
      createShipment,
      markShipmentReceived,
      registerMovement,
      createTender,
      addTenderOffer,
      assignTenderId,
      awardTender,
      addTenderAttachments,
      updateTenderApproval,
      adjustInventory,
      uploadSupplierDocument,
      updateSupplierCompliance,
      evaluateSupplier,
      reviewSupplier,
      importMaterialsFromCsv,
      importSuppliersFromCsv,
      markNotificationAsRead,
      removeNotification,
      pushNotification,
      addAuditRecord,
      findUserById,
      findParkById,
      findAreaById,
      findCostCenterById,
      getProjectBudgetSummary,
      projectFinancialOverview,
      upsertProjectBudgetAllocation,
      removeProjectBudgetAllocation,
      upsertProjectIncome,
      getProjectTimeline,
      addProjectTimelineTask,
      updateProjectStatusItem,
      resetProjectStatusMatrix,
      updateRenewableProjectStage,
      updateRenewablePermit,
      updateRenewableProjectApproval,
      updateRenewableRisk,
      addSupplier,
      approveSupplier,
      createLegalCase,
      updateLegalCase,
      addLegalCaseMilestone,
      assignLawyersToCase,
      createLegalDocument,
      updateLegalContract,
      upsertLegalComplianceAction,
      updateLegalPower,
      updateLawyerProfile,
      upsertLegalReportMetric,
      renewableProjectMap,
      createRfQ,
      receiveRfQInPurchasing,
      validateRfQCompleteness,
      requestRfQCorrection,
      assignRfQBuyer,
      launchRfQQuotation,
      selectRfQPreferredSupplier,
      generateRfQPurchaseOrder,
      markRfQPoForApproval,
      recordRfQPoDecision,
      markRfQPoSent,
      closeRfQ,
      cancelRfQ
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error(t('useAppContext debe utilizarse dentro de un AppProvider'));
  }
  return context;
};
